import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogOverlay, DialogPortal } from './ui/dialog';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { AppHeader } from '../App';
import { Checkbox } from './ui/checkbox';
import { Separator } from './ui/separator';
import { AlertCircle } from 'lucide-react';
import { 
  Users, 
  CreditCard, 
  BarChart3, 
  Plus, 
  Search, 
  Edit, 
  Eye,
  UserCheck,
  UserX,
  Crown,
  Shield,
  User,
  Key,
  Mail,
  UserPlus,
  Trash2,
  RefreshCw,
  Clock,
  CheckCircle,
  ExternalLink,
  Minus,
  XCircle,
  Store,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api`;

const AdminDashboard = ({ user, onLogout }) => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showAddCredits, setShowAddCredits] = useState(false);
  const [creditsAmount, setCreditsAmount] = useState('');
  const [creditsDescription, setCreditsDescription] = useState('');
  const [userTransactions, setUserTransactions] = useState([]);
  const [allTransactions, setAllTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    role: 'regular',
    company_id: '',
    // Company profile (Bedrijfsgegevens)
    company_name: '',
    company_street: '',
    company_number: '',
    company_city: '',
    company_postal_code: '',
    company_vat_number: '',
    email_template_suffix: '',
    send_credentials_email: true
  });
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);

  // Location access management state
  const [regions, setRegions] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedProvince, setSelectedProvince] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [userPermissions, setUserPermissions] = useState([]);
  const [selectedUserForAccess, setSelectedUserForAccess] = useState(null);
  const [userSearch, setUserSearch] = useState('');
  const [showAddPermission, setShowAddPermission] = useState(false);
  const [newPermission, setNewPermission] = useState({
    user_id: '',
    permission_type: 'province',
    province_ids: [],  // Changed to array for multiple selections
    city_ids: []       // Changed to array for multiple selections
  });
  const [zipcodeSearch, setZipcodeSearch] = useState('');
  
  // Category management state
  const [categories, setCategories] = useState([]);
  const [userCategoryPermissions, setUserCategoryPermissions] = useState([]);
  const [showAddCategoryPermission, setShowAddCategoryPermission] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [selectAllGroups, setSelectAllGroups] = useState({});
  
  // Distribution state
  const [distributingProjects, setDistributingProjects] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  
  // Exact Online state
  const [exactConfig, setExactConfig] = useState({
    client_id: '',
    client_secret: '',
    base_url: 'https://start.exactonline.nl',
    redirect_uri: '',
    default_department: '',
    credit_purchase_document_layout: null,
    is_configured: false,
    is_connected: false,
    access_token: '',
    refresh_token: '',
    token_expires_at: null
  });
  const [exactDepartments, setExactDepartments] = useState([]);
  const [documentLayouts, setDocumentLayouts] = useState([]);
  const [loadingLayouts, setLoadingLayouts] = useState(false);
  const [exactLoading, setExactLoading] = useState(false);
  const [exactError, setExactError] = useState(null);
  const [showExactAuth, setShowExactAuth] = useState(false);
  
  // VAT Lookup state
  const [vatNumber, setVatNumber] = useState('');
  const [vatLookupData, setVatLookupData] = useState(null);
  const [vatLookupLoading, setVatLookupLoading] = useState(false);
  const [vatLookupError, setVatLookupError] = useState(null);
  
  // Module access management state
  const [modules, setModules] = useState([]);
  const [moduleSearchTerm, setModuleSearchTerm] = useState('');
  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [exactCompanyCheckDialog, setExactCompanyCheckDialog] = useState(false);
  const [foundExactCompany, setFoundExactCompany] = useState(null);
  const [pendingSyncUserId, setPendingSyncUserId] = useState(null);

  // Debug: Log when dialog state changes
  useEffect(() => {
    console.log('exactCompanyCheckDialog state changed to:', exactCompanyCheckDialog);
    console.log('foundExactCompany:', foundExactCompany);
    console.log('pendingSyncUserId:', pendingSyncUserId);
  }, [exactCompanyCheckDialog, foundExactCompany, pendingSyncUserId]);
  const [selectedUserForModules, setSelectedUserForModules] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [savingModules, setSavingModules] = useState(false);
  const [moduleError, setModuleError] = useState('');

  // Check if user is provided
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">No User Data</h1>
          <p className="text-gray-600">Unable to load admin dashboard. Please try logging in again.</p>
        </div>
      </div>
    );
  }

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('token');
  };

  // API call helper
  const apiCall = async (endpoint, options = {}) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await fetch(`${API}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage = `HTTP error! status: ${response.status}`;
      
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.detail || errorJson.message || errorMessage;
      } catch (e) {
        errorMessage = errorText || errorMessage;
      }
      
      const error = new Error(errorMessage);
      error.status = response.status;
      error.detail = errorMessage;
      throw error;
    }

    return response.json();
  };

  // Download invoice helper
  const [downloadingTxMap, setDownloadingTxMap] = useState({});
  const downloadInvoice = async (paymentId, filename, txId) => {
    try {
      if (!paymentId) return;
      if (txId) {
        setDownloadingTxMap(prev => ({ ...prev, [txId]: true }));
      }
      const token = getAuthToken();
      const res = await fetch(`${API}/payments/${paymentId}/invoice`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || `invoice_${paymentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Failed to download invoice:', e);
      alert('Failed to download invoice');
    } finally {
      if (txId) {
        setDownloadingTxMap(prev => {
          const next = { ...prev };
          delete next[txId];
          return next;
        });
      }
    }
  };

  // Load users
  const loadUsers = async () => {
    try {
      const data = await apiCall('/admin/users');
      setUsers(data);
      setError(null);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users: ' + error.message);
    }
  };

  // Load stats
  const loadStats = async () => {
    try {
      const data = await apiCall('/admin/stats');
      setStats(data);
      setError(null);
    } catch (error) {
      console.error('Error loading stats:', error);
      setError('Failed to load stats: ' + error.message);
    }
  };

  // Load user transactions
  const loadUserTransactions = async (userId) => {
    try {
      const data = await apiCall(`/admin/users/${userId}/transactions`);
      // The API returns an object with transactions array, not a direct array
      const transactions = data.transactions || data;
      setUserTransactions(Array.isArray(transactions) ? transactions : []);
    } catch (error) {
      console.error('Error loading user transactions:', error);
      setUserTransactions([]);
    }
  };

  // Load all transactions
  const loadAllTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const data = await apiCall('/admin/transactions');
      setAllTransactions(Array.isArray(data.transactions) ? data.transactions : []);
    } catch (error) {
      console.error('Error loading all transactions:', error);
      setAllTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Add credits to user
  const addCredits = async (userId, amount, description) => {
    try {
      const parsedAmount = parseInt(amount);
      
      await apiCall(`/admin/users/${userId}/credits`, {
        method: 'POST',
        body: JSON.stringify({ amount: parsedAmount, description }),
      });
      await loadUsers(); // Reload users to show updated credits
      setShowAddCredits(false);
      setCreditsAmount('');
      setCreditsDescription('');
    } catch (error) {
      console.error('Error adding credits:', error);
      alert('Error adding credits: ' + error.message);
    }
  };

  // Update user role
  const updateUserRole = async (userId, newRole) => {
    try {
      await apiCall(`/admin/users/${userId}/role`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole }),
      });
      await loadUsers(); // Reload users
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Error updating user role: ' + error.message);
    }
  };

  // Update user status
  const updateUserStatus = async (userId, isActive) => {
    try {
      await apiCall(`/admin/users/${userId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: isActive }),
      });
      await loadUsers(); // Reload users
    } catch (error) {
      console.error('Error updating user status:', error);
      alert('Error updating user status: ' + error.message);
    }
  };

  // Create new user
  const createUser = async () => {
    try {
      if (!newUser.email || !newUser.full_name) {
        alert('Please fill in email and full name');
        return;
      }

      const response = await apiCall('/admin/users', {
        method: 'POST',
        body: JSON.stringify(newUser),
      });

      setShowCreateUser(false);
      setNewUser({
        email: '',
        full_name: '',
        role: 'regular',
        company_id: '',
        company_name: '',
        company_street: '',
        company_number: '',
        company_city: '',
        company_postal_code: '',
        company_vat_number: '',
        email_template_suffix: '',
        send_credentials_email: true
      });
      
      // Clear VAT lookup data
      setVatLookupData(null);
      setVatLookupError(null);
      setVatNumber('');
      await loadUsers(); // Reload users
      alert('User created successfully!');
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user: ' + error.message);
    }
  };

  // Reset user password
  const resetUserPassword = async (userId) => {
    try {
      const response = await apiCall(`/admin/users/${userId}/reset-password`, {
        method: 'POST',
      });
      
      setResetPasswordResult(response);
      await loadUsers(); // Reload users
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Error resetting password: ' + error.message);
    }
  };

  // Delete user
  const deleteUser = async (userId) => {
    try {
      const response = await apiCall(`/admin/users/${userId}`, {
        method: 'DELETE',
      });
      
      setDeleteResult(response);
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      await loadUsers(); // Reload users
      alert('User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error deleting user: ' + error.message);
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (userToDelete) => {
    setUserToDelete(userToDelete);
    setShowDeleteConfirm(true);
    setDeleteResult(null);
  };

  // Location access management functions
  const loadRegions = async () => {
    try {
      const data = await apiCall('/location/regions');
      setRegions(data);
    } catch (error) {
      console.error('Error loading regions:', error);
      setError('Failed to load regions');
    }
  };

  const loadProvinces = async (regionId) => {
    try {
      let data;
      if (regionId) {
        data = await apiCall(`/location/regions/${regionId}/provinces`);
      } else {
        // Load all provinces if no region specified
        data = await apiCall('/location/provinces');
      }
      setProvinces(data);
      console.log('🌍 Provinces loaded:', data.length);
      console.log('   Sample provinces:', data.slice(0, 3).map(p => `${p.name} (ID: ${p.id}, type: ${typeof p.id})`));
    } catch (error) {
      console.error('Error loading provinces:', error);
      setError('Failed to load provinces');
    }
  };

  const loadCities = async (provinceId) => {
    try {
      const data = await apiCall(`/location/cities?province_id=${provinceId}`);
      // Merge with existing cities from other provinces
      setCities(prevCities => {
        const existingCityIds = new Set(prevCities.map(c => c.id));
        const newCities = data.filter(c => !existingCityIds.has(c.id));
        return [...prevCities, ...newCities];
      });
      console.log('Cities loaded for province:', provinceId, data.length);
    } catch (error) {
      console.error('Error loading cities:', error);
      setError('Failed to load cities');
    }
  };

  // Load cities for multiple selected provinces
  const loadCitiesForProvinces = async (provinceIds, zipcodeFilter = '') => {
    try {
      console.log('🔍 Loading cities for province IDs:', provinceIds);
      console.log('🔍 Zipcode filter:', zipcodeFilter);
      setCities([]); // Clear existing cities
      const allCities = [];
      
      for (const provinceId of provinceIds) {
        console.log(`📍 Fetching cities for province ${provinceId}...`);
        let url = `/location/cities?province_id=${provinceId}`;
        if (zipcodeFilter) {
          url += `&zipcode_search=${encodeURIComponent(zipcodeFilter)}`;
        }
        const data = await apiCall(url);
        console.log(`✅ Got ${data.length} cities for province ${provinceId}`);
        console.log('   Sample cities:', data.slice(0, 3).map(c => `${c.name} (${c.zipcode})`));
        allCities.push(...data);
      }
      
      setCities(allCities);
      console.log(`🎯 Total cities loaded: ${allCities.length} cities for ${provinceIds.length} province(s)`);
    } catch (error) {
      console.error('❌ Error loading cities:', error);
      setError('Failed to load cities');
    }
  };

  const loadUserPermissions = async (userId) => {
    try {
      const data = await apiCall(`/location/users/${userId}/permissions`);
      setUserPermissions(data.permissions);
    } catch (error) {
      console.error('Error loading user permissions:', error);
      setError('Failed to load user permissions');
    }
  };

  const createUserPermission = async () => {
    try {
      if (!newPermission.user_id) {
        setError('Please select a user');
        return;
      }

      if (newPermission.permission_type === 'province' && newPermission.province_ids.length === 0) {
        setError('Please select at least one province');
        return;
      }

      if (newPermission.permission_type === 'city' && newPermission.city_ids.length === 0) {
        setError('Please select at least one city');
        return;
      }

      // Create permissions for each selected province or city
      const permissionsToCreate = [];
      
      if (newPermission.permission_type === 'province') {
        for (const province_id of newPermission.province_ids) {
          permissionsToCreate.push({
            user_id: newPermission.user_id,
            permission_type: 'province',
            province_id: province_id,
            city_id: null
          });
        }
      } else {
        for (const city_id of newPermission.city_ids) {
          permissionsToCreate.push({
            user_id: newPermission.user_id,
            permission_type: 'city',
            province_id: null,
            city_id: city_id
          });
        }
      }

      // Create all permissions
      let successCount = 0;
      let errorCount = 0;
      let lastError = null;
      
      console.log('Creating permissions:', permissionsToCreate);
      
      for (const permission of permissionsToCreate) {
        try {
          console.log('Sending permission:', permission);
          await apiCall(`/location/users/${newPermission.user_id}/permissions`, {
            method: 'POST',
            body: JSON.stringify(permission)
          });
          successCount++;
        } catch (error) {
          console.error('Error creating permission:', error, permission);
          lastError = error;
          errorCount++;
          // Continue with other permissions even if one fails
        }
      }

      setShowAddPermission(false);
      setZipcodeSearch('');
      setCities([]);
      setNewPermission({
        user_id: selectedUserForAccess?.id || '',
        permission_type: 'province',
        province_ids: [],
        city_ids: []
      });
      
      if (selectedUserForAccess) {
        await loadUserPermissions(selectedUserForAccess.id);
      }
      
      if (errorCount > 0) {
        const errorMsg = lastError?.message || 'Unknown error';
        setError(`Created ${successCount} permissions, ${errorCount} failed. Last error: ${errorMsg}`);
      } else {
        setError(null);
      }
    } catch (error) {
      console.error('Error creating permissions:', error);
      setError('Failed to create permissions');
    }
  };

  const deleteUserPermission = async (permissionId) => {
    try {
      console.log('🗑️ Deleting permission:', permissionId);
      const result = await apiCall(`/location/permissions/${permissionId}`, {
        method: 'DELETE'
      });
      console.log('✅ Permission deleted:', result);
      
      if (selectedUserForAccess) {
        await loadUserPermissions(selectedUserForAccess.id);
      }
      
      setError(null);
      alert('Permission deleted successfully!');
    } catch (error) {
      console.error('❌ Error deleting permission:', error);
      console.error('Error details:', error.message, error.detail);
      setError(`Failed to delete permission: ${error.message || error.detail || 'Unknown error'}`);
      alert(`Failed to delete permission: ${error.message || error.detail || 'Unknown error'}`);
    }
  };

  // Exact Online functions
  const loadExactConfig = async () => {
    try {
      const data = await apiCall('/admin/exact-online/config');
      setExactConfig(data);
    } catch (error) {
      console.error('Error loading Exact Online config:', error);
      setExactError('Failed to load Exact Online configuration');
    }
  };

  const saveExactConfig = async () => {
    try {
      setExactLoading(true);
      setExactError(null);
      
      const response = await apiCall('/admin/exact-online/config', {
        method: 'POST',
        body: JSON.stringify(exactConfig)
      });
      
      setExactConfig(response);
      alert('Exact Online configuration saved successfully!');
    } catch (error) {
      console.error('Error saving Exact Online config:', error);
      setExactError('Failed to save Exact Online configuration: ' + error.message);
    } finally {
      setExactLoading(false);
    }
  };

  const loadDocumentLayouts = async () => {
    if (!exactConfig.is_connected) return;
    
    try {
      setLoadingLayouts(true);
      const data = await apiCall('/admin/exact-online/document-layouts?document_type=11'); // 21 = Sales Order
      setDocumentLayouts(data || []);
    } catch (error) {
      console.error('Error loading document layouts:', error);
      setDocumentLayouts([]);
    } finally {
      setLoadingLayouts(false);
    }
  };

  const loadExactDepartments = async () => {
    try {
      setExactLoading(true);
      const data = await apiCall('/admin/exact-online/departments');
      setExactDepartments(data);
    } catch (error) {
      console.error('Error loading divisions:', error);
      setExactError('Failed to load divisions: ' + error.message);
    } finally {
      setExactLoading(false);
    }
  };

  const initiateExactAuth = async () => {
    try {
      setExactLoading(true);
      setExactError(null);
      
      const response = await apiCall('/admin/exact-online/auth/initiate', {
        method: 'POST'
      });
      
      // Open popup window for authorization
      console.log("🚀 Opening popup with URL:", response.auth_url);
      const authWindow = window.open(
        response.auth_url,
        'exactAuth',
        'width=600,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no'
      );
      
      if (!authWindow) {
        throw new Error('Popup blocked. Please allow popups for this site.');
      }
      
      console.log("✅ Popup opened successfully");
      
      // Listen for messages from the popup window
      const messageListener = (event) => {
        console.log("📨 Message received from popup:", event);
        console.log("📨 Event origin:", event.origin);
        console.log("📨 Event data:", event.data);
        
        // Security check - only accept messages from Exact Online domains and your callback
        const allowedOrigins = [
          'https://start.exactonline.nl',
          'https://start.exactonline.be', 
          'https://start.exactonline.de',
          'https://login.mijnperceel.be',
          window.location.origin
        ];
        
        console.log("📨 Allowed origins:", allowedOrigins);
        console.log("📨 Is origin allowed?", allowedOrigins.includes(event.origin));
        
        if (!allowedOrigins.includes(event.origin)) {
          console.log("❌ Origin not allowed, ignoring message");
          return;
        }
        
        if (event.data && event.data.code) {
          console.log("🔑 Auth code received from your callback:", event.data.code);
          console.log("🔑 Calling handleAuthorizationCode...");
          handleAuthorizationCode(event.data.code);
          window.removeEventListener('message', messageListener);
          authWindow.close();
        } else if (event.data && event.data.type === 'EXACT_AUTH_ERROR') {
          console.log("❌ Auth error received:", event.data.error);
          setExactError(event.data.error || 'Authorization failed');
          setExactLoading(false);
          window.removeEventListener('message', messageListener);
          authWindow.close();
        } else {
          console.log("❓ Unknown message format from your callback:", event.data);
          console.log("❓ Expected format: { code: 'authorization_code' }");
        }
      };
      
      // Add message listener
      console.log("👂 Setting up message listener for your callback");
      window.addEventListener('message', messageListener);
      
      // Check if popup was closed manually
      const checkClosed = setInterval(() => {
        if (authWindow.closed) {
          console.log("❌ Popup was closed manually");
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          setExactLoading(false);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Error initiating Exact Online auth:', error);
      setExactError('Failed to initiate authentication: ' + error.message);
      setExactLoading(false);
    }
  };

  const handleAuthorizationCode = async (code) => {
    try {
      console.log("🔑 handleAuthorizationCode called with code:", code);
      setExactLoading(true);
      setExactError(null);
      
      console.log("🔑 Sending code to backend for token exchange...");
      const response = await apiCall('/admin/exact-online/auth/callback', {
        method: 'POST',
        body: JSON.stringify({ code })
      });
      
      console.log("🔑 Token exchange response:", response);
      
      setExactConfig(prev => ({
        ...prev,
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        token_expires_at: response.expires_at,
        is_connected: true
      }));
      
      setExactLoading(false);
      alert('Successfully connected to Exact Online!');
      
    } catch (error) {
      console.error('Error handling authorization code:', error);
      setExactError('Failed to complete authentication: ' + error.message);
      setExactLoading(false);
    }
  };

  const refreshExactToken = async () => {
    try {
      setExactLoading(true);
      setExactError(null);
      
      const response = await apiCall('/admin/exact-online/auth/refresh', {
        method: 'POST'
      });
      
      setExactConfig(prev => ({
        ...prev,
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        token_expires_at: response.expires_at,
        is_connected: true
      }));
      
      alert('Token refreshed successfully!');
    } catch (error) {
      console.error('Error refreshing token:', error);
      setExactError('Failed to refresh token: ' + error.message);
    } finally {
      setExactLoading(false);
    }
  };

  const performSync = async (userId, useExistingCompany = false) => {
    try {
      setExactLoading(true);
      const displayUser = users.find(u => u.id === userId) || selectedUser;
      const codeVal = String(selectedUser?.exact_company_code ?? displayUser?.exact_company_code ?? '').trim();
      const idVal = String(selectedUser?.exact_company_id ?? displayUser?.exact_company_id ?? '').trim();
      
      const payload = {
        exact_company_id: useExistingCompany && foundExactCompany ? foundExactCompany.id : (idVal || null),
        company_code: codeVal || null,
        force_create_new: !useExistingCompany && foundExactCompany ? true : false
      };
      
      await apiCall(`/admin/exact-online/sync/user/${userId}`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      await loadUsers();
      alert('User synced to Exact Online successfully');
      setExactCompanyCheckDialog(false);
      setFoundExactCompany(null);
      setPendingSyncUserId(null);
    } catch (err) {
      console.error('Failed syncing user to Exact Online', err);
      alert('Sync failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setExactLoading(false);
    }
  };

  const syncClientsToExact = async () => {
    try {
      setExactLoading(true);
      setExactError(null);
      
      const response = await apiCall('/admin/exact-online/sync/clients', {
        method: 'POST'
      });
      
      alert(`Successfully synced ${response.synced_count} clients to Exact Online!`);
    } catch (error) {
      console.error('Error syncing clients:', error);
      setExactError('Failed to sync clients: ' + error.message);
    } finally {
      setExactLoading(false);
    }
  };

  // Category management functions
  const loadCategories = async () => {
    try {
      console.log('📋 Loading categories...');
      const data = await apiCall('/location/categories');
      console.log('📋 Categories loaded:', data);
      console.log('📋 Number of categories:', data?.length || 0);
      
      // Ensure data is an array
      if (Array.isArray(data)) {
        setCategories(data);
        if (data.length === 0) {
          console.warn('⚠️ No categories returned from API - the categories table might be empty');
          console.warn('⚠️ Please run the populate_categories.py script to add categories to the database');
        }
      } else {
        console.error('❌ Categories data is not an array:', typeof data, data);
        setCategories([]);
        setError('Categories data format is invalid');
      }
    } catch (error) {
      console.error('❌ Error loading categories:', error);
      console.error('   Error details:', error.message, error.detail);
      console.error('   Error status:', error.status);
      setCategories([]);
      setError(`Failed to load categories: ${error.message}`);
    }
  };

  const loadUserCategoryPermissions = async (userId) => {
    try {
      const data = await apiCall(`/location/users/${userId}/category-permissions`);
      setUserCategoryPermissions(data.permissions);
    } catch (error) {
      console.error('Error loading user category permissions:', error);
      setError('Failed to load user category permissions');
    }
  };

  // Helper function to filter users based on search term
  const getFilteredUsers = () => {
    if (!userSearch.trim()) {
      return users;
    }
    
    const searchTerm = userSearch.toLowerCase();
    return users.filter(user => 
      user.full_name?.toLowerCase().includes(searchTerm) ||
      user.email?.toLowerCase().includes(searchTerm) ||
      user.role?.toLowerCase().includes(searchTerm)
    );
  };

  // Helper functions for category grouping
  const toggleGroupExpansion = (groupId) => {
    const newExpandedGroups = new Set(expandedGroups);
    if (newExpandedGroups.has(groupId)) {
      newExpandedGroups.delete(groupId);
    } else {
      newExpandedGroups.add(groupId);
    }
    setExpandedGroups(newExpandedGroups);
  };

  // Helper function to get categories in a group
  const getCategoriesInGroup = (groupId, groupData, categorySearch) => {
    return groupData.categories.filter(category => 
      categorySearch === '' || 
      category.name.toLowerCase().includes(categorySearch.toLowerCase())
    );
  };

  // Helper function to check if all categories in a group are selected
  const areAllCategoriesInGroupSelected = (groupId, groupData, categorySearch) => {
    const filteredCategories = getCategoriesInGroup(groupId, groupData, categorySearch);
    if (filteredCategories.length === 0) return false;
    
    return filteredCategories.every(category => 
      selectedCategories.includes(category.id) || 
      userCategoryPermissions.some(perm => perm.category_id === category.id)
    );
  };

  // Helper function to check if some (but not all) categories in a group are selected
  const areSomeCategoriesInGroupSelected = (groupId, groupData, categorySearch) => {
    const filteredCategories = getCategoriesInGroup(groupId, groupData, categorySearch);
    if (filteredCategories.length === 0) return false;
    
    const selectedCount = filteredCategories.filter(category => 
      selectedCategories.includes(category.id) || 
      userCategoryPermissions.some(perm => perm.category_id === category.id)
    ).length;
    
    return selectedCount > 0 && selectedCount < filteredCategories.length;
  };

  // Handle select all for a group
  const handleSelectAllGroup = (groupId, groupData, categorySearch, checked) => {
    const filteredCategories = getCategoriesInGroup(groupId, groupData, categorySearch);
    const availableCategories = filteredCategories.filter(category => 
      !userCategoryPermissions.some(perm => perm.category_id === category.id)
    );
    
    if (checked) {
      // Add all available categories to selection
      const newSelectedCategories = [...selectedCategories];
      availableCategories.forEach(category => {
        if (!newSelectedCategories.includes(category.id)) {
          newSelectedCategories.push(category.id);
        }
      });
      setSelectedCategories(newSelectedCategories);
    } else {
      // Remove all categories in this group from selection
      const newSelectedCategories = selectedCategories.filter(categoryId => 
        !filteredCategories.some(category => category.id === categoryId)
      );
      setSelectedCategories(newSelectedCategories);
    }
  };

  const getCategoriesByGroup = () => {
    console.log('🔍 getCategoriesByGroup called');
    console.log('📊 Categories data:', categories);
    console.log('📊 First few categories:', categories.slice(0, 3));
    
    const groupedCategories = {};
    
    // Define the category groups based on the migration data
    const predefinedGroups = [
      { id: 1, name: "Gebouwen (hoofdvolume)", description: "Main building volume categories", sort_order: 1 },
      { id: 2, name: "Bijgebouwen & lage (niet-overdekte) constructies", description: "Annexes and low uncovered structures", sort_order: 2 },
      { id: 3, name: "Functie & Gebruik", description: "Function and use categories", sort_order: 3 },
      { id: 4, name: "Sloop & Verwijderen", description: "Demolition and removal categories", sort_order: 4 },
      { id: 5, name: "Complexen & Verkaveling", description: "Complexes and subdivision categories", sort_order: 5 },
      { id: 6, name: "Infrastructuur & Wegen", description: "Infrastructure and roads categories", sort_order: 6 },
      { id: 7, name: "Recreatie", description: "Recreation categories", sort_order: 7 },
      { id: 8, name: "Publiciteitsinrichtingen", description: "Advertising installations categories", sort_order: 8 },
      { id: 9, name: "Kleinhandel", description: "Retail categories", sort_order: 9 },
      { id: 10, name: "Bodem & Water", description: "Soil and water categories", sort_order: 10 },
      { id: 11, name: "Landschap & Natuur", description: "Landscape and nature categories", sort_order: 11 },
      { id: 12, name: "Tijdelijke constructies", description: "Temporary constructions categories", sort_order: 12 }
    ];
    
    // Initialize all groups
    predefinedGroups.forEach(group => {
      groupedCategories[group.id] = {
        group: group,
        categories: []
      };
    });
    
    // Add ungrouped categories
    groupedCategories['ungrouped'] = {
      group: { id: 'ungrouped', name: 'Other Categories', description: 'Categories not assigned to any group' },
      categories: []
    };
    
    // Group categories
    categories.forEach(category => {
      console.log(`🔍 Processing category: ${category.name}, group_id: ${category.category_group_id}`);
      if (category.category_group_id && groupedCategories[category.category_group_id]) {
        groupedCategories[category.category_group_id].categories.push(category);
        console.log(`✅ Added to group ${category.category_group_id}`);
      } else {
        groupedCategories['ungrouped'].categories.push(category);
        console.log(`⚠️ Added to ungrouped`);
      }
    });
    
    console.log('📊 Final grouped categories:', groupedCategories);
    return groupedCategories;
  };

  const createUserCategoryPermissions = async () => {
    try {
      if (!selectedUserForAccess) {
        setError('Please select a user');
        return;
      }

      if (selectedCategories.length === 0) {
        setError('Please select at least one category');
        return;
      }

      let successCount = 0;
      let errorCount = 0;
      let lastError = null;

      for (const categoryId of selectedCategories) {
        try {
          await apiCall(`/location/users/${selectedUserForAccess.id}/category-permissions`, {
            method: 'POST',
            body: JSON.stringify({ category_id: categoryId })
          });
          successCount++;
        } catch (error) {
          console.error('Error creating category permission:', error);
          lastError = error;
          errorCount++;
        }
      }

      setShowAddCategoryPermission(false);
      setSelectedCategories([]);
      setCategorySearch('');

      if (selectedUserForAccess) {
        await loadUserCategoryPermissions(selectedUserForAccess.id);
      }

      if (errorCount > 0) {
        const errorMsg = lastError?.message || 'Unknown error';
        setError(`Created ${successCount} permissions, ${errorCount} failed. Last error: ${errorMsg}`);
      } else {
        setError(null);
        alert(`Successfully added ${successCount} category permission(s)!`);
      }
    } catch (error) {
      console.error('Error creating category permissions:', error);
      setError('Failed to create category permissions');
    }
  };

  const deleteUserCategoryPermission = async (permissionId) => {
    try {
      await apiCall(`/location/category-permissions/${permissionId}`, {
        method: 'DELETE'
      });

      if (selectedUserForAccess) {
        await loadUserCategoryPermissions(selectedUserForAccess.id);
      }

      setError(null);
      alert('Category permission deleted successfully!');
    } catch (error) {
      console.error('Error deleting category permission:', error);
      setError(`Failed to delete category permission: ${error.message || error.detail || 'Unknown error'}`);
      alert(`Failed to delete category permission: ${error.message || error.detail || 'Unknown error'}`);
    }
  };

  // Module access management functions
  const loadModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModules(response.data || []);
    } catch (err) {
      console.error('Failed to load modules:', err);
      setModules([]);
    }
  };

  const openModuleDialogForUser = async (targetUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users/${targetUser.id}/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUserForModules(targetUser);
      setSelectedModules(response.data?.modules || []);
      setModuleDialogOpen(true);
      setModuleError('');
    } catch (err) {
      console.error('Failed to load user modules:', err);
      setModuleError('Kon moduletoegang niet ophalen');
    }
  };

  const toggleModule = (moduleId, checked) => {
    setSelectedModules((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, moduleId]));
      }
      return prev.filter((id) => id !== moduleId);
    });
  };

  const handleSaveModules = async () => {
    if (!selectedUserForModules) return;
    setSavingModules(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/users/${selectedUserForModules.id}/modules`,
        { modules: selectedModules },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModuleDialogOpen(false);
      setModuleError('');
      // Refresh users to show updated module access
      loadUsers();
    } catch (err) {
      console.error('Failed to save modules:', err);
      setModuleError(err.response?.data?.detail || 'Kon moduletoegang niet opslaan');
    } finally {
      setSavingModules(false);
    }
  };

  const filteredUsersForModules = useMemo(() => {
    if (!Array.isArray(users) || users.length === 0) {
      return [];
    }
    const term = moduleSearchTerm.trim().toLowerCase();
    if (!term) {
      return users;
    }
    return users.filter((item) => {
      const name = (item.full_name || '').toLowerCase();
      const email = (item.email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [users, moduleSearchTerm]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([loadUsers(), loadStats(), loadAllTransactions(), loadRegions(), loadCategories(), loadExactConfig(), loadModules()]);
      setLoading(false);
    };
    loadData();
  }, []);

  // Auto-load divisions when connected to Exact Online
  useEffect(() => {
    if (exactConfig.is_connected && exactDepartments.length === 0) {
      loadExactDepartments();
    }
  }, [exactConfig.is_connected, exactConfig.default_department, exactDepartments.length]);
  
  // Auto-load document layouts when connected and default department is set
  useEffect(() => {
    if (exactConfig.is_connected && exactConfig.default_department && documentLayouts.length === 0) {
      loadDocumentLayouts();
    }
  }, [exactConfig.is_connected, exactConfig.default_department, documentLayouts.length]);
  
  // Also load divisions when config is loaded with a default_department but divisions are not loaded yet
  useEffect(() => {
    if (exactConfig.default_department && exactDepartments.length === 0 && exactConfig.is_connected) {
      loadExactDepartments();
    }
  }, [exactConfig.default_department]);

  // VAT Lookup function
  const lookupVatNumber = async () => {
    if (!vatNumber.trim()) {
      setVatLookupError('Please enter a BTW nummer');
      return;
    }

    try {
      setVatLookupLoading(true);
      setVatLookupError(null);
      
      const data = await apiCall(`/vat-lookup/check/${encodeURIComponent(vatNumber.trim())}`);
      setVatLookupData(data);
    } catch (error) {
      console.error('Error looking up BTW nummer:', error);
      setVatLookupError('Failed to lookup BTW nummer: ' + (error.message || 'Unknown error'));
    } finally {
      setVatLookupLoading(false);
    }
  };

  const applyVatDataToUser = (userData) => {
    if (!userData || !selectedUser) return;
    
    const updates = {};
    
    // Map VAT data to user fields
    if (userData.company_name) updates.company_name = userData.company_name;
    if (userData.street) updates.company_street = userData.street;
    if (userData.street_number) updates.company_number = userData.street_number;
    if (userData.postal_code) updates.company_postal_code = userData.postal_code;
    if (userData.city) updates.company_city = userData.city;
    if (userData.vat_number) updates.company_vat_number = userData.vat_number;
    
    setSelectedUser(prev => ({ ...prev, ...updates }));
  };

  const applyVatDataToNewUser = (userData) => {
    if (!userData) return;
    
    const updates = {};
    
    // Map VAT data to new user fields
    if (userData.company_name) updates.company_name = userData.company_name;
    if (userData.street) updates.company_street = userData.street;
    if (userData.street_number) updates.company_number = userData.street_number;
    if (userData.postal_code) updates.company_postal_code = userData.postal_code;
    if (userData.city) updates.company_city = userData.city;
    if (userData.vat_number) updates.company_vat_number = userData.vat_number;
    
    setNewUser(prev => ({ ...prev, ...updates }));
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => 
    user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get role icon
  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'admin': return <Shield className="h-4 w-4 text-blue-500" />;
      case 'reseller': return <Store className="h-4 w-4 text-green-500" />;
      default: return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  // Get role badge color
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-yellow-100 text-yellow-800';
      case 'admin': return 'bg-blue-100 text-blue-800';
      case 'reseller': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Loading admin dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Error</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AppHeader 
        user={user} 
        currentPage="admin"
        onNavigate={(path) => window.location.href = path}
        onLogout={onLogout}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="users">User Management</TabsTrigger>
            <TabsTrigger value="subscriptions">Subscriptions</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="location-access">Location Access</TabsTrigger>
            <TabsTrigger value="modules">Modules</TabsTrigger>
            <TabsTrigger value="exact-online">Exact Online</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                    <Users className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.users.total}</div>
                    <p className="text-xs text-muted-foreground">
                      {stats.users.active} active, {stats.users.inactive} inactive
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.credits.total_in_system.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">
                      In system
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Credits Spent</CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.credits.total_spent.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">
                      Total spent
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Transactions</CardTitle>
                    <BarChart3 className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{stats.credits.total_transactions}</div>
                    <p className="text-xs text-muted-foreground">
                      Total transactions
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* User Roles Breakdown */}
            {stats && (
              <Card>
                <CardHeader>
                  <CardTitle>User Roles</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="flex items-center space-x-2">
                      <Crown className="h-5 w-5 text-yellow-500" />
                      <span className="text-sm font-medium">Super Admins</span>
                      <Badge className={getRoleBadgeColor('super_admin')}>
                        {stats.users.by_role.super_admin || 0}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Shield className="h-5 w-5 text-blue-500" />
                      <span className="text-sm font-medium">Admins</span>
                      <Badge className={getRoleBadgeColor('admin')}>
                        {stats.users.by_role.admin || 0}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Store className="h-5 w-5 text-green-500" />
                      <span className="text-sm font-medium">Resellers</span>
                      <Badge className={getRoleBadgeColor('reseller')}>
                        {stats.users.by_role.reseller || 0}
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <User className="h-5 w-5 text-gray-500" />
                      <span className="text-sm font-medium">Regular Users</span>
                      <Badge className={getRoleBadgeColor('regular')}>
                        {stats.users.by_role.regular || 0}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* User Management Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>Manage users, roles, and credits</CardDescription>
                  </div>
                  <div className="flex space-x-2">
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search users..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-8 w-64"
                      />
                    </div>
                    <Button onClick={() => setShowCreateUser(true)}>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Create User
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {filteredUsers.map((displayUser) => (
                    <div key={displayUser.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          {getRoleIcon(displayUser.role)}
                          <div>
                            <div className="font-medium">{displayUser.full_name}</div>
                            <div className="text-sm text-muted-foreground">{displayUser.email}</div>
                          </div>
                        </div>
                        <Badge className={getRoleBadgeColor(displayUser.role)}>
                          {displayUser.role.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <Badge variant={displayUser.is_active ? "default" : "secondary"}>
                          {displayUser.is_active ? "Active" : "Inactive"}
                        </Badge>
                        <div className="text-sm font-medium">
                          {displayUser.credits} credits
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Dialog onOpenChange={(open) => {
                          if (!open) {
                            // Clear VAT lookup data when dialog is closed
                            setVatLookupData(null);
                            setVatLookupError(null);
                            setVatNumber('');
                          }
                        }}>
                          <DialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(displayUser);
                                loadUserTransactions(displayUser.id);
                                // Clear VAT lookup data when opening edit user
                                setVatLookupData(null);
                                setVatLookupError(null);
                                setVatNumber('');
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-3xl w-[95vw] md:w-[720px] max-h-[75vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>User Details</DialogTitle>
                              <DialogDescription>
                                Manage {displayUser.full_name}'s account
                              </DialogDescription>
                            </DialogHeader>
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <Label>Name</Label>
                                  <div className="text-sm font-medium">{displayUser.full_name}</div>
                                </div>
                                <div>
                                  <Label>Email</Label>
                                  <div className="text-sm font-medium">{displayUser.email}</div>
                                </div>
                                <div>
                                  <Label>Role</Label>
                                  <Select
                                    value={displayUser.role}
                                    onValueChange={(value) => updateUserRole(displayUser.id, value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="regular">Regular</SelectItem>
                                      <SelectItem value="admin">Admin</SelectItem>
                                      <SelectItem value="super_admin">Super Admin</SelectItem>
                                      <SelectItem value="reseller">Reseller</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Status</Label>
                                  <div className="flex items-center space-x-2">
                                    <Switch
                                      checked={displayUser.is_active}
                                      onCheckedChange={(checked) => updateUserStatus(displayUser.id, checked)}
                                    />
                                    <span className="text-sm">
                                      {displayUser.is_active ? "Active" : "Inactive"}
                                    </span>
                                  </div>
                                </div>
                                <div>
                                  <Label>Email Template Suffix</Label>
                                  <Select
                                    value={displayUser.email_template_suffix || 'none'}
                                    onValueChange={async (value) => {
                                      try {
                                        await apiCall(`/admin/users/${displayUser.id}/email-template-suffix`, {
                                          method: 'PATCH',
                                          body: JSON.stringify({ email_template_suffix: value === 'none' ? null : value })
                                        });
                                        await loadUsers();
                                      } catch (err) {
                                        console.error('Failed to update email template suffix', err);
                                        alert('Failed to update email template suffix: ' + (err?.message || 'Unknown error'));
                                      }
                                    }}
                                  >
                                    <SelectTrigger>
                                      <SelectValue placeholder="Select template suffix" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">None (default templates)</SelectItem>
                                      <SelectItem value="mlp">MLP</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label>Credits</Label>
                                  <div className="text-sm font-medium">{displayUser.credits}</div>
                                </div>
                                <div>
                                  <Label>Created</Label>
                                  <div className="text-sm font-medium">
                                    {new Date(displayUser.created_at).toLocaleDateString()}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Password Reset Section */}
                              <div className="border-t pt-4">
                                <h4 className="font-medium mb-2">Password Management</h4>
                                <div className="flex space-x-2">
                                  <Button
                                    variant="outline"
                                    onClick={() => resetUserPassword(displayUser.id)}
                                    className="flex-1"
                                  >
                                    <Key className="h-4 w-4 mr-1" />
                                    Reset Password
                                  </Button>
                                </div>
                                {resetPasswordResult && (
                                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md">
                                    <div className="text-sm text-green-800">
                                      <strong>Password Reset Successful!</strong>
                                    </div>
                                    <div className="text-xs text-green-600 mt-1">
                                      {resetPasswordResult.message}
                                    </div>
                                    {resetPasswordResult.new_password && (
                                      <div className="text-xs text-green-600 mt-1">
                                        <strong>New Password:</strong> {resetPasswordResult.new_password}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>

                              {/* Delete User Section */}
                              <div className="border-t pt-4">
                                <h4 className="font-medium mb-2 text-red-600">Danger Zone</h4>
                                <div className="flex space-x-2">
                                  <Button
                                    variant="destructive"
                                    onClick={() => handleDeleteClick(displayUser)}
                                    className="flex-1"
                                    disabled={displayUser.id === user.id}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Delete User
                                  </Button>
                                </div>
                                {displayUser.id === user.id && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    You cannot delete your own account
                                  </div>
                                )}
                              </div>

                             
                              {/* Bedrijfsgegevens (Company) Edit */}
                              <div className="border-t pt-4">
                                <h4 className="font-medium mb-2">Bedrijfsgegevens</h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                  <div className="md:col-span-2">
                                    <Label>Bedrijfsnaam</Label>
                                    <Input
                                      placeholder="Bedrijfsnaam"
                                      value={(selectedUser?.company_name ?? displayUser.company_name) || ''}
                                      onChange={(e) => setSelectedUser(prev => ({ ...prev, company_name: e.target.value }))}
                                    />
                                  </div>
                                  <div className="md:col-span-2">
                                    <Label>Straat</Label>
                                    <Input
                                      placeholder="Straat"
                                      value={(selectedUser?.company_street ?? displayUser.company_street) || ''}
                                      onChange={(e) => setSelectedUser(prev => ({ ...prev, company_street: e.target.value }))}
                                    />
                                  </div>
                                  <div>
                                    <Label>Nummer</Label>
                                    <Input
                                      placeholder="Nummer"
                                      value={(selectedUser?.company_number ?? displayUser.company_number) || ''}
                                      onChange={(e) => setSelectedUser(prev => ({ ...prev, company_number: e.target.value }))}
                                    />
                                  </div>
                                  <div>
                                    <Label>Postcode</Label>
                                    <Input
                                      placeholder="Postcode"
                                      value={(selectedUser?.company_postal_code ?? displayUser.company_postal_code) || ''}
                                      onChange={(e) => setSelectedUser(prev => ({ ...prev, company_postal_code: e.target.value }))}
                                    />
                                  </div>
                                  <div className="md:col-span-2">
                                    <Label>Stad</Label>
                                    <Input
                                      placeholder="Stad"
                                      value={(selectedUser?.company_city ?? displayUser.company_city) || ''}
                                      onChange={(e) => setSelectedUser(prev => ({ ...prev, company_city: e.target.value }))}
                                    />
                                  </div>
                                  <div className="md:col-span-2">
                                    <Label>BTW Nummer</Label>
                                    <Input
                                      placeholder="BTW Nummer"
                                      value={(selectedUser?.company_vat_number ?? displayUser.company_vat_number) || ''}
                                      onChange={(e) => setSelectedUser(prev => ({ ...prev, company_vat_number: e.target.value }))}
                                    />
                                  </div>
                                </div>
                                <div className="flex justify-end mt-3">
                                  <Button
                                    variant="outline"
                                    onClick={async () => {
                                      try {
                                        const body = {
                                          company_name: (selectedUser?.company_name ?? displayUser.company_name) || null,
                                          company_street: (selectedUser?.company_street ?? displayUser.company_street) || null,
                                          company_number: (selectedUser?.company_number ?? displayUser.company_number) || null,
                                          company_city: (selectedUser?.company_city ?? displayUser.company_city) || null,
                                          company_postal_code: (selectedUser?.company_postal_code ?? displayUser.company_postal_code) || null,
                                          company_vat_number: (selectedUser?.company_vat_number ?? displayUser.company_vat_number) || null,
                                        };
                                        await apiCall(`/admin/users/${displayUser.id}/company`, {
                                          method: 'PUT',
                                          body: JSON.stringify(body)
                                        });
                                        await loadUsers();
                                        alert('Bedrijfsgegevens opgeslagen');
                                      } catch (err) {
                                        console.error('Failed saving company data', err);
                                        alert('Opslaan mislukt: ' + (err?.message || 'Onbekende fout'));
                                      }
                                    }}
                                  >
                                    Bedrijfsgegevens Opslaan
                                  </Button>
                                </div>
                              </div>

                              {/* BTW Nummer Lookup Section */}
                              <div className="border-t pt-4">
                                <h4 className="font-medium mb-2">BTW Nummer Lookup</h4>
                                <div className="space-y-4">
                                  <div className="flex space-x-2">
                                    <Input
                                      placeholder="Enter BTW nummer (e.g., BE0123456789)"
                                      value={vatNumber}
                                      onChange={(e) => setVatNumber(e.target.value)}
                                      className="flex-1"
                                    />
                                    <Button
                                      onClick={lookupVatNumber}
                                      disabled={vatLookupLoading}
                                      variant="outline"
                                    >
                                      {vatLookupLoading ? (
                                        <>
                                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                          Looking up...
                                        </>
                                      ) : (
                                        <>
                                          <Search className="h-4 w-4 mr-2" />
                                          Lookup
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                  
                                  {vatLookupError && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                      <p className="text-sm text-red-600">{vatLookupError}</p>
                                    </div>
                                  )}
                                  
                                  {vatLookupData && vatLookupData.is_valid && (
                                    <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                                      <h5 className="font-medium text-green-800 mb-2">Company Found:</h5>
                                      <div className="space-y-1 text-sm">
                                        {vatLookupData.company_name && (
                                          <p><strong>Name:</strong> {vatLookupData.company_name}</p>
                                        )}
                                        {vatLookupData.street && (
                                          <p><strong>Street:</strong> {vatLookupData.street}</p>
                                        )}
                                        {vatLookupData.street_number && (
                                          <p><strong>Number:</strong> {vatLookupData.street_number}</p>
                                        )}
                                        {vatLookupData.postal_code && (
                                          <p><strong>Postal Code:</strong> {vatLookupData.postal_code}</p>
                                        )}
                                        {vatLookupData.city && (
                                          <p><strong>City:</strong> {vatLookupData.city}</p>
                                        )}
                                        {vatLookupData.vat_number && (
                                          <p><strong>BTW Nummer:</strong> {vatLookupData.vat_number}</p>
                                        )}
                                        {vatLookupData.address && (
                                          <p><strong>Full Address:</strong> {vatLookupData.address}</p>
                                        )}
                                      </div>
                                      <Button
                                        onClick={() => applyVatDataToUser(vatLookupData)}
                                        className="mt-3"
                                        size="sm"
                                      >
                                        Apply to User
                                      </Button>
                                    </div>
                                  )}
                                  
                                  {vatLookupData && !vatLookupData.is_valid && vatLookupData.error_message && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                                      <p className="text-sm text-red-600">{vatLookupData.error_message}</p>
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Exact Online Sync Section */}
                              <div className="border-t pt-4">
                                <h4 className="font-medium mb-2">Exact Online Synchronisatie</h4>
                                <div className="space-y-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    <div>
                                      <Label>Exact Online Bedrijfs ID</Label>
                                      <Input
                                        placeholder="Voer Exact Online Bedrijfs ID in"
                                        value={selectedUser?.exact_company_id ?? displayUser.exact_company_id ?? ''}
                                        onChange={(e) => setSelectedUser(prev => ({ ...prev, exact_company_id: e.target.value }))}
                                      />
                                    </div>
                                    <div>
                                      <Label>Exact Client Code (Exact Klantcode)</Label>
                                      <Input
                                        placeholder="Voer Exact Klantcode in (bijv. klantcode)"
                                        value={selectedUser?.exact_company_code ?? displayUser.exact_company_code ?? ''}
                                        onChange={(e) => setSelectedUser(prev => ({ ...prev, exact_company_code: e.target.value }))}
                                      />
                                      <p className="text-xs text-muted-foreground mt-1">
                                        Als het Bedrijfs ID onbekend is, kunt u synchroniseren via de Klantcode.
                                      </p>
                                    </div>
                                  </div>

                                  <div>
                                    <Button
                                      variant="outline"
                                      onClick={async () => {
                                        try {
                                          const codeVal = String(selectedUser?.exact_company_code ?? displayUser.exact_company_code ?? '').trim();
                                          const idVal = String(selectedUser?.exact_company_id ?? displayUser.exact_company_id ?? '').trim();
                                          const body = {
                                            exact_company_id: idVal || null,
                                            exact_company_code: codeVal || null,
                                          };
                                          await apiCall(`/admin/users/${displayUser.id}/exact-company`, {
                                            method: 'PUT',
                                            body: JSON.stringify(body)
                                          });
                                          await loadUsers();
                                          alert("Exact ID's saved");
                                        } catch (err) {
                                          console.error('Failed saving Exact identifiers', err);
                                          alert('Save failed: ' + (err?.message || 'Unknown error'));
                                        }
                                      }}
                                      className="w-full"
                                    >
                                       Exact Id's Opslaan
                                    </Button>
                                  </div>
                                  
                                  <div>
                                    <Button
                                      variant="outline"
                                      onClick={async () => {
                                        try {
                                          setExactLoading(true);
                                          // First check if company exists in Exact Online
                                          const checkResponse = await apiCall(`/admin/exact-online/check-company/${displayUser.id}`, {
                                            method: 'GET'
                                          });
                                          
                                          console.log('Check company response:', checkResponse);
                                          console.log('Response exists:', checkResponse?.exists);
                                          console.log('Response company:', checkResponse?.company);
                                          console.log('Response type:', typeof checkResponse);
                                          
                                          // Check if response is valid and has company
                                          if (checkResponse && (checkResponse.exists === true || checkResponse.exists === 'true') && checkResponse.company) {
                                            // Company exists, show dialog
                                            console.log('Company found, showing dialog:', checkResponse.company);
                                            
                                            // Set all state at once to ensure React batches the updates
                                            setFoundExactCompany(checkResponse.company);
                                            setPendingSyncUserId(displayUser.id);
                                            setExactCompanyCheckDialog(true);
                                            
                                            setExactLoading(false);
                                            return; // Exit early to prevent sync
                                          } else {
                                            // No company found, proceed with sync (will create new)
                                            console.log('No company found or invalid response, proceeding with sync');
                                            console.log('Response check:', {
                                              hasResponse: !!checkResponse,
                                              exists: checkResponse?.exists,
                                              existsType: typeof checkResponse?.exists,
                                              hasCompany: !!checkResponse?.company,
                                              companyType: typeof checkResponse?.company
                                            });
                                            await performSync(displayUser.id, false);
                                          }
                                        } catch (err) {
                                          console.error('Failed checking company in Exact Online', err);
                                          console.error('Error details:', {
                                            message: err?.message,
                                            status: err?.status,
                                            detail: err?.detail,
                                            response: err?.response
                                          });
                                          // If check fails, proceed with sync anyway
                                          await performSync(displayUser.id, false);
                                        } finally {
                                          setExactLoading(false);
                                        }
                                      }}
                                      disabled={exactLoading}
                                      className="w-full"
                                    >
                                      {exactLoading ? (
                                        <>
                                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                          Syncing...
                                        </>
                                      ) : (
                                        <>
                                          <RefreshCw className="h-4 w-4 mr-2" />
                                          Gebruiker Synchroniseren met Exact Online
                                        </>
                                      )}
                                    </Button>
                                  </div>
                                </div>
                              </div>

                               {/* Add Credits Section */}
                               <div className="border-t pt-4">
                                <h4 className="font-medium mb-2">Add Credits</h4>
                                <div className="flex space-x-2">
                                  <Input
                                    type="number"
                                    placeholder="Amount"
                                    value={creditsAmount}
                                    onChange={(e) => setCreditsAmount(e.target.value)}
                                    className="flex-1"
                                  />
                                  <Input
                                    placeholder="Description"
                                    value={creditsDescription}
                                    onChange={(e) => setCreditsDescription(e.target.value)}
                                    className="flex-1"
                                  />
                                  <Button
                                    onClick={() => addCredits(displayUser.id, creditsAmount, creditsDescription)}
                                    disabled={!creditsAmount || !creditsDescription}
                                  >
                                    <Plus className="h-4 w-4 mr-1" />
                                    Add
                                  </Button>
                                </div>
                              </div>


                              {/* Recent Transactions */}
                              <div className="border-t pt-4">
                                <h4 className="font-medium mb-2">Recent Transactions</h4>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                  {userTransactions && userTransactions.length > 0 ? (
                                    userTransactions.slice(0, 5).map((transaction, index) => (
                                      <div key={transaction.id || index} className="flex justify-between items-center text-sm p-2 bg-gray-50 rounded">
                                        <div className="pr-3">
                                          <span className="font-medium">
                                            {transaction.transaction_type === 'credit' ? '+' : '-'}
                                            {Math.abs(transaction.amount)} credits
                                          </span>
                                          <div className="text-muted-foreground">
                                            {transaction.description || 'No description'}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {transaction.payment_id && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => downloadInvoice(transaction.payment_id, `invoice_${transaction.payment_id}.pdf`, transaction.id)}
                                              disabled={!!downloadingTxMap[transaction.id]}
                                            >
                                              {!!downloadingTxMap[transaction.id] ? 'Downloading...' : 'Download invoice'}
                                            </Button>
                                          )}
                                          <div className="text-muted-foreground">
                                            {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'Unknown date'}
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                  ) : (
                                    <div className="text-sm text-muted-foreground text-center py-4">
                                      {userTransactions === null ? 'Loading transactions...' : 'No transactions found'}
                                    </div>
                                  )}
                                </div>
                              </div>

                              {/* Credits Section */}
                              <div className="border-t pt-4">
                                <h4 className="font-medium mb-2">Credits Beheer</h4>
                                <div className="space-y-4">
                                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div>
                                      <p className="text-sm font-medium">Huidige Credits</p>
                                      <p className="text-2xl font-bold text-blue-600">{displayUser.credits || 0}</p>
                                    </div>
                                    <div className="flex space-x-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const amount = prompt('Voer het aantal credits in om toe te voegen:');
                                          if (amount && !isNaN(amount) && parseFloat(amount) > 0) {
                                            addCredits(displayUser.id, parseFloat(amount), 'Admin credit addition');
                                          }
                                        }}
                                      >
                                        <Plus className="h-4 w-4 mr-1" />
                                        Credits Toevoegen
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const amount = prompt('Voer het aantal credits in om te verwijderen:');
                                          if (amount && !isNaN(amount) && parseFloat(amount) > 0) {
                                            addCredits(displayUser.id, -parseFloat(amount), 'Admin credit removal');
                                          } else if (amount) {
                                            alert('Voer een geldig positief getal in');
                                          }
                                        }}
                                      >
                                        <Minus className="h-4 w-4 mr-1" />
                                        Credits Verwijderen
                                      </Button>
                                    </div>
                                  </div>
                                  
                                  <div className="text-xs text-gray-500">
                                    <p>• Credits kunnen worden toegevoegd of verwijderd door beheerders</p>
                                    <p>• Alle credit wijzigingen worden gelogd in de transactiegeschiedenis</p>
                                  </div>
                                </div>
                              </div>

                              {/* Subscription Management Section */}
                              <UserSubscriptionManagement
                                userId={displayUser.id}
                                userName={displayUser.full_name}
                                API={API}
                                loadUsers={loadUsers}
                              />
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>All Transactions</CardTitle>
                <CardDescription>View all credit transactions across the system</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingTransactions ? (
                  <div className="text-center py-8">
                    <div className="text-lg">Loading transactions...</div>
                  </div>
                ) : allTransactions.length > 0 ? (
                  <div className="space-y-4">
                    {allTransactions.map((transaction) => (
                      <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              transaction.transaction_type === 'credit' ? 'bg-green-500' : 
                              transaction.transaction_type === 'debit' ? 'bg-red-500' : 'bg-yellow-500'
                            }`}></div>
                            <div>
                              <div className="font-medium">
                                {transaction.user_name} ({transaction.user_email})
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {transaction.description || 'No description'}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {transaction.payment_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadInvoice(transaction.payment_id, `invoice_${transaction.payment_id}.pdf`, transaction.id)}
                              disabled={!!downloadingTxMap[transaction.id]}
                            >
                              {!!downloadingTxMap[transaction.id] ? 'Downloading...' : 'Download invoice'}
                            </Button>
                          )}
                          <div className="text-right">
                          <div className={`font-medium ${
                            transaction.transaction_type === 'credit' ? 'text-green-600' : 
                            transaction.transaction_type === 'debit' ? 'text-red-600' : 'text-yellow-600'
                          }`}>
                            {transaction.transaction_type === 'credit' ? '+' : '-'}
                            {Math.abs(transaction.amount)} credits
                          </div>
                          <div className="text-sm text-muted-foreground">
                            Balance: {transaction.balance_after}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(transaction.created_at).toLocaleString()}
                          </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No transactions found
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Subscriptions Tab */}
          <TabsContent value="subscriptions" className="space-y-6">
            <SubscriptionManagement 
              API={API}
              users={users}
              refreshUsers={loadUsers}
            />
          </TabsContent>

          {/* Location Access Tab */}
          <TabsContent value="location-access" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>User Location Access Management</CardTitle>
                <CardDescription>
                  Configure which provinces and cities each user can access for MijnPerceel projects
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* User Selection */}
                <div className="space-y-4">
                  <div className="space-y-3">
                    <Label htmlFor="user-select">Select User</Label>
                    
                    {/* User Search Input */}
                    <div className="relative">
                      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search users by name, email, or role..."
                        value={userSearch}
                        onChange={(e) => setUserSearch(e.target.value)}
                        className="pl-8 pr-8"
                      />
                      {userSearch && (
                        <button
                          onClick={() => setUserSearch('')}
                          className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 hover:text-gray-600"
                        >
                          ×
                        </button>
                      )}
                    </div>
                    
                    {/* User Selection Dropdown */}
                    <Select
                      value={selectedUserForAccess?.id || ''}
                      onValueChange={(userId) => {
                        const user = users.find(u => u.id === userId);
                        setSelectedUserForAccess(user);
                        setNewPermission(prev => ({ ...prev, user_id: userId }));
                        setUserSearch(''); // Clear search when user is selected
                        if (user) {
                          loadUserPermissions(user.id);
                          loadUserCategoryPermissions(user.id);
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a user to manage access" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {getFilteredUsers().length === 0 ? (
                          <div className="p-2 text-sm text-gray-500 text-center">
                            {userSearch ? 'No users found matching your search' : 'No users available'}
                          </div>
                        ) : (
                          getFilteredUsers().map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              <div className="flex flex-col">
                                <span className="font-medium">{user.full_name}</span>
                                <span className="text-xs text-gray-500">{user.email} - {user.role}</span>
                              </div>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    
                    {/* Search Results Info */}
                    {userSearch && (
                      <p className="text-xs text-gray-500">
                        Showing {getFilteredUsers().length} of {users.length} users
                      </p>
                    )}
                  </div>

                  {selectedUserForAccess && (
                    <div className="space-y-4">
                      {/* Current Permissions */}
                      <div>
                        <h3 className="text-lg font-medium mb-3">Current Permissions</h3>
                        {userPermissions.length > 0 ? (
                          <div className="space-y-2">
                            {userPermissions.map((permission) => (
                              <div key={permission.id} className="flex items-center justify-between p-3 border rounded-lg">
                                <div className="flex items-center space-x-3">
                                  <Badge variant={permission.permission_type === 'province' ? 'default' : 'secondary'}>
                                    {permission.permission_type}
                                  </Badge>
                                  <div>
                                    {permission.permission_type === 'province' && permission.province && (
                                      <span className="font-medium">{permission.province.name}</span>
                                    )}
                                    {permission.permission_type === 'city' && permission.city && (
                                      <span className="font-medium">{permission.city.name} ({permission.city.zipcode})</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Badge variant={permission.is_active ? 'default' : 'secondary'}>
                                    {permission.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteUserPermission(permission.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-muted-foreground">
                            No permissions assigned to this user
                          </div>
                        )}
                        
                        {/* Distribute Projects Button */}
                        {userPermissions.length > 0 && (
                          <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-medium text-sm mb-1">Distribute Projects</h4>
                                <p className="text-xs text-gray-600">
                                  Convert MijnPerceel projects from the last 20 days to leads for this user
                                </p>
                                {distributingProjects && (
                                  <p className="text-xs text-emerald-600 font-medium mt-2 animate-pulse">
                                    ⏳ Processing... Please wait
                                  </p>
                                )}
                              </div>
                              <Button
                                variant="default"
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={distributingProjects}
                                onClick={async () => {
                                  setDistributingProjects(true);
                                  try {
                                    const token = localStorage.getItem('token');
                                    const response = await fetch(`${API}/mijnperceel/distribute-projects-to-user/${selectedUserForAccess.id}?days_back=20`, {
                                      method: 'POST',
                                      headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json'
                                      }
                                    });
                                    
                                    if (!response.ok) {
                                      throw new Error(`Failed to distribute: ${response.statusText}`);
                                    }
                                    
                                    const result = await response.json();
                                    alert(`✓ Distributed ${result.projects_distributed} projects to ${result.user_email}\n\nAccessible zipcodes: ${result.accessible_zipcodes}\nAfter zipcode filter: ${result.projects_after_zipcode_filter}\nAfter category filter: ${result.projects_after_category_filter}`);
                                  } catch (error) {
                                    console.error('Distribution failed:', error);
                                    alert(`❌ Failed to distribute projects: ${error.message}`);
                                  } finally {
                                    setDistributingProjects(false);
                                  }
                                }}
                              >
                                <RefreshCw className={`h-4 w-4 mr-2 ${distributingProjects ? 'animate-spin' : ''}`} />
                                {distributingProjects ? 'Distributing...' : 'Distribute Projects (20 days)'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Add New Permission */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-medium">Add New Location Permission</h3>
                          <Button onClick={() => {
                            setShowAddPermission(true);
                            setCities([]);  // Clear cities from previous selections
                            setZipcodeSearch('');  // Clear zipcode search
                            // Load provinces for Vlaanderen region (ID: 1) when opening dialog
                            loadProvinces('1');  // Vlaanderen region ID
                          }}>
                            <Plus className="h-4 w-4 mr-2" />
                            Add Location Permission
                          </Button>
                        </div>
                      </div>

                      {/* Category Permissions Section */}
                      <div className="border-t pt-6 mt-6">
                        <h3 className="text-lg font-medium mb-3">Category Permissions</h3>
                        {userCategoryPermissions.length > 0 ? (
                          <div className="space-y-2 mb-4">
                            {userCategoryPermissions.map((permission) => (
                              <div key={permission.id} className="flex items-center justify-between p-3 border rounded-lg bg-blue-50">
                                <div className="flex items-center space-x-3">
                                  <Badge variant="secondary">Category</Badge>
                                  <span className="font-medium text-sm">{permission.category?.name}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Badge variant={permission.is_active ? 'default' : 'secondary'}>
                                    {permission.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => deleteUserCategoryPermission(permission.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-4 text-muted-foreground mb-4">
                            No category permissions assigned to this user
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            Manage which project categories this user can access
                          </p>
                          <Button onClick={() => setShowAddCategoryPermission(true)} variant="outline">
                            <Plus className="h-4 w-4 mr-2" />
                            Add Category Permissions
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Modules Tab */}
          <TabsContent value="modules" className="space-y-6">
            {moduleError && (
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <p className="text-sm text-red-700">{moduleError}</p>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle>Moduletoegang beheren</CardTitle>
                <CardDescription>
                  Bepaal welke modules elke gebruiker mag zien binnen ProjectLeads.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <Input
                    value={moduleSearchTerm}
                    onChange={(e) => setModuleSearchTerm(e.target.value)}
                    placeholder="Zoek op naam of e-mailadres..."
                  />
                </div>
                {loading ? (
                  <div className="text-center py-12 text-gray-500">Gegevens laden...</div>
                ) : (
                  <div className="space-y-4">
                    {filteredUsersForModules.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        Geen gebruikers gevonden. Pas je zoekopdracht aan.
                      </div>
                    ) : (
                      filteredUsersForModules.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-start justify-between p-4 border border-gray-200 rounded-lg"
                        >
                          <div>
                            <h3 className="font-semibold text-gray-900">{item.full_name}</h3>
                            <p className="text-sm text-gray-600">{item.email}</p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant={item.role === 'admin' ? 'default' : item.role === 'super_admin' ? 'destructive' : 'secondary'}>
                                {item.role}
                              </Badge>
                              <Badge variant={item.is_active ? 'default' : 'destructive'}>
                                {item.is_active ? 'Actief' : 'Inactief'}
                              </Badge>
                            </div>
                          </div>
                          <Button variant="outline" onClick={() => openModuleDialogForUser(item)}>
                            Moduletoegang aanpassen
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            <Dialog open={moduleDialogOpen} onOpenChange={setModuleDialogOpen}>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Moduletoegang aanpassen</DialogTitle>
                  <DialogDescription>
                    Bepaal welke pagina&apos;s {selectedUserForModules?.full_name} kan bekijken.
                  </DialogDescription>
                </DialogHeader>

                <Separator className="my-4" />

                <div className="space-y-4">
                  {modules.length === 0 ? (
                    <p className="text-sm text-gray-600">Geen modules gevonden.</p>
                  ) : (
                    modules.map((module) => (
                      <label
                        key={module.id}
                        className="flex items-center justify-between border border-gray-200 rounded-lg p-3 hover:border-emerald-300 transition-colors cursor-pointer"
                      >
                        <div>
                          <p className="font-medium text-gray-900">{module.label}</p>
                          {module.description && (
                            <p className="text-sm text-gray-600">{module.description}</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">Module-id: {module.id}</p>
                        </div>
                        <Checkbox
                          checked={selectedModules.includes(module.id)}
                          onCheckedChange={(checked) => toggleModule(module.id, checked)}
                        />
                      </label>
                    ))
                  )}
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <Button variant="outline" onClick={() => setModuleDialogOpen(false)}>
                    Annuleren
                  </Button>
                  <Button onClick={handleSaveModules} disabled={savingModules}>
                    {savingModules ? 'Opslaan...' : 'Opslaan'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Exact Online Tab */}
          <TabsContent value="exact-online" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Exact Online Integration</CardTitle>
                <CardDescription>
                  Configure and manage your Exact Online integration for automated invoicing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {exactError && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                    <div className="text-sm text-red-800">{exactError}</div>
                  </div>
                )}

                {/* Configuration Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="client_id">Client ID</Label>
                      <Input
                        id="client_id"
                        placeholder="Your Exact Online Client ID"
                        value={exactConfig.client_id}
                        onChange={(e) => setExactConfig(prev => ({ ...prev, client_id: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="client_secret">Client Secret</Label>
                      <Input
                        id="client_secret"
                        type="password"
                        placeholder="Your Exact Online Client Secret"
                        value={exactConfig.client_secret}
                        onChange={(e) => setExactConfig(prev => ({ ...prev, client_secret: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label htmlFor="base_url">Base URL</Label>
                      <Select
                        value={exactConfig.base_url}
                        onValueChange={(value) => setExactConfig(prev => ({ ...prev, base_url: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="https://start.exactonline.nl">Netherlands (start.exactonline.nl)</SelectItem>
                          <SelectItem value="https://start.exactonline.be">Belgium (start.exactonline.be)</SelectItem>
                          <SelectItem value="https://start.exactonline.de">Germany (start.exactonline.de)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="redirect_uri">Redirect URI</Label>
                      <Input
                        id="redirect_uri"
                        placeholder="https://yourdomain.com/auth/exact/callback"
                        value={exactConfig.redirect_uri}
                        onChange={(e) => setExactConfig(prev => ({ ...prev, redirect_uri: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <Button 
                      onClick={saveExactConfig} 
                      disabled={exactLoading || !exactConfig.client_id || !exactConfig.client_secret}
                    >
                      {exactLoading ? 'Saving...' : 'Save Configuration'}
                    </Button>
                  </div>
                </div>

                {/* Authentication Section */}
                {exactConfig.is_configured && (
                  <div className="space-y-4 border-t pt-6">
                    <h3 className="text-lg font-medium">Authentication</h3>
                    
                    {exactConfig.is_connected ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="h-5 w-5 text-green-600" />
                            <span className="text-sm font-medium text-green-800">Connected to Exact Online</span>
                          </div>
                          <div className="text-xs text-green-600 mt-1">
                            Token expires: {exactConfig.token_expires_at ? new Date(exactConfig.token_expires_at).toLocaleString() : 'Unknown'}
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            onClick={refreshExactToken}
                            disabled={exactLoading}
                          >
                            <RefreshCw className={`h-4 w-4 mr-2 ${exactLoading ? 'animate-spin' : ''}`} />
                            Refresh Token
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={loadExactDepartments}
                            disabled={exactLoading}
                          >
                            <Search className="h-4 w-4 mr-2" />
                            Load Divisions
                          </Button>
                          <Button 
                            variant="outline" 
                            onClick={loadDocumentLayouts}
                            disabled={loadingLayouts || exactLoading}
                          >
                            <Search className={`h-4 w-4 mr-2 ${loadingLayouts ? 'animate-spin' : ''}`} />
                            Load Templates
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                          <div className="flex items-center space-x-2">
                            <Clock className="h-5 w-5 text-yellow-600" />
                            <span className="text-sm font-medium text-yellow-800">Not Connected</span>
                          </div>
                          <div className="text-xs text-yellow-600 mt-1">
                            Click "Authorize with Exact Online" to connect your account
                          </div>
                        </div>
                        
                        <Button 
                          onClick={initiateExactAuth}
                          disabled={exactLoading}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          {exactLoading ? 'Opening Authorization...' : 'Authorize with Exact Online'}
                        </Button>
                        {exactLoading && (
                          <p className="text-xs text-yellow-600 mt-2">
                            A popup window will open for authorization. Please complete the process there.
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Division Selection */}
                {((exactConfig.is_connected && exactDepartments.length > 0) || exactDepartments.length > 0) ? (
                  <div className="space-y-4 border-t pt-6">
                    <h3 className="text-lg font-medium">Default Division</h3>
                    <div>
                      <Label htmlFor="default_department">Select Default Division</Label>
                      <Select
                        value={exactConfig.default_department || undefined}
                        onValueChange={(value) => setExactConfig(prev => ({ ...prev, default_department: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Choose a default department">
                            {exactConfig.default_department && exactDepartments.length > 0 ? (() => {
                              const selectedDept = exactDepartments.find(
                                dept => (dept.ID && dept.ID.toString() === exactConfig.default_department) || 
                                        (dept.Code && dept.Code.toString() === exactConfig.default_department)
                              );
                              if (selectedDept) {
                                const displayName = selectedDept.Description && selectedDept.Description.trim() !== '' 
                                  ? selectedDept.Description 
                                  : (selectedDept.Code && selectedDept.Code.trim() !== '' ? `Division ${selectedDept.Code}` : `Division ${exactConfig.default_department}`);
                                const displayCode = selectedDept.Code && selectedDept.Code.trim() !== '' ? selectedDept.Code : exactConfig.default_department;
                                return `${displayName} (${displayCode})`;
                              }
                              return exactConfig.default_department;
                            })() : exactConfig.default_department || "Choose a default department"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {exactDepartments
                            .filter(dept => (dept.ID && dept.ID.toString().trim() !== '') || (dept.Code && dept.Code.toString().trim() !== ''))
                            .map((dept, index) => {
                              const displayId = dept.ID && dept.ID.toString().trim() !== '' ? dept.ID : dept.Code;
                              const displayName = dept.Description && dept.Description.trim() !== '' 
                                ? dept.Description 
                                : (dept.Code && dept.Code.trim() !== '' ? `Division ${dept.Code}` : `Division ${displayId}`);
                              const displayCode = dept.Code && dept.Code.trim() !== '' ? dept.Code : displayId;
                              return (
                                <SelectItem key={displayId || `dept-${index}`} value={displayId.toString()}>
                                  {displayName} ({displayCode})
                                </SelectItem>
                              );
                            })}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button 
                        onClick={saveExactConfig} 
                        disabled={exactLoading}
                      >
                        {exactLoading ? 'Saving...' : 'Save Department'}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {/* Document Layout Template Section */}
                {exactConfig.is_connected && exactConfig.default_department && (
                  <div className="space-y-4 border-t pt-6">
                    <h3 className="text-lg font-medium">Aankoop Credits Template</h3>
                    <div>
                      <Label htmlFor="credit_purchase_document_layout">Select Template for Credit Purchases</Label>
                      {loadingLayouts ? (
                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-md">
                          <p className="text-sm text-gray-600">Loading templates...</p>
                        </div>
                      ) : documentLayouts.length === 0 ? (
                        <div className="space-y-2">
                          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                            <p className="text-sm text-yellow-800">
                              No templates loaded. Click "Load Templates" button above to load available templates.
                            </p>
                          </div>
                          {exactConfig.credit_purchase_document_layout && (
                            <div className="p-2 bg-blue-50 border border-blue-200 rounded-md">
                              <p className="text-xs text-blue-800">
                                Current selection: {exactConfig.credit_purchase_document_layout}
                              </p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Select
                          value={exactConfig.credit_purchase_document_layout || undefined}
                          onValueChange={(value) => setExactConfig(prev => ({ 
                            ...prev, 
                            credit_purchase_document_layout: value === 'none' ? null : value 
                          }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Choose a template">
                              {exactConfig.credit_purchase_document_layout && documentLayouts.length > 0 ? (() => {
                                const selected = documentLayouts.find(
                                  layout => layout.ID === exactConfig.credit_purchase_document_layout
                                );
                                return selected ? (selected.Subject || selected.ID) : exactConfig.credit_purchase_document_layout;
                              })() : "Choose a template"}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None (Use Default)</SelectItem>
                            {documentLayouts.map((layout) => (
                              <SelectItem key={layout.ID} value={layout.ID}>
                                {layout.Subject || layout.ID}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        This template will be used when generating PDF invoices for credit purchases.
                      </p>
                    </div>
                    
                    {documentLayouts.length > 0 && (
                      <div className="flex justify-end">
                        <Button 
                          onClick={saveExactConfig} 
                          disabled={exactLoading}
                        >
                          {exactLoading ? 'Saving...' : 'Save Template'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Client Sync Section */}
                {exactConfig.is_connected && exactConfig.default_department && (
                  <div className="space-y-4 border-t pt-6">
                    <h3 className="text-lg font-medium">Client Synchronization</h3>
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                      <div className="text-sm text-blue-800">
                        <strong>Sync your clients to Exact Online</strong>
                        <p className="mt-1 text-xs">
                          This will create or update contacts and companies in Exact Online based on your user database.
                          Each user will be synced as a contact, and their company information will be synced as a company.
                        </p>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={syncClientsToExact}
                      disabled={exactLoading}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${exactLoading ? 'animate-spin' : ''}`} />
                      {exactLoading ? 'Syncing...' : 'Sync Clients to Exact Online'}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Exact Company Check Dialog - Outside tabs to avoid z-index issues */}
        <Dialog 
          open={exactCompanyCheckDialog} 
          onOpenChange={(open) => {
            console.log('Dialog onOpenChange called with:', open);
            setExactCompanyCheckDialog(open);
            if (!open) {
              setFoundExactCompany(null);
              setPendingSyncUserId(null);
            }
          }}
        >
          <DialogPortal>
            <DialogOverlay className="!z-[9998]" />
            <DialogPrimitive.Content className="fixed left-[50%] top-[50%] !z-[9999] grid w-full max-w-2xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg">
            <DialogHeader>
              <DialogTitle>Bedrijf gevonden in Exact Online</DialogTitle>
              <DialogDescription>
                Er is al een bedrijf gevonden in Exact Online met hetzelfde BTW-nummer.
              </DialogDescription>
            </DialogHeader>

            {foundExactCompany && (
              <div className="space-y-4 py-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-900 mb-2">Gevonden bedrijf:</h4>
                  <div className="space-y-1 text-sm">
                    <p><strong>Naam:</strong> {foundExactCompany.name}</p>
                    {foundExactCompany.vat_number && (
                      <p><strong>BTW-nummer:</strong> {foundExactCompany.vat_number}</p>
                    )}
                    {foundExactCompany.email && (
                      <p><strong>Email:</strong> {foundExactCompany.email}</p>
                    )}
                    {foundExactCompany.phone && (
                      <p><strong>Telefoon:</strong> {foundExactCompany.phone}</p>
                    )}
                    {(foundExactCompany.address_line1 || foundExactCompany.city) && (
                      <p><strong>Adres:</strong> {[foundExactCompany.address_line1, foundExactCompany.postcode, foundExactCompany.city].filter(Boolean).join(', ')}</p>
                    )}
                  </div>
                </div>

                <p className="text-sm text-gray-600">
                  Wil je dit bestaande bedrijf gebruiken of een nieuw bedrijf aanmaken in Exact Online?
                </p>
              </div>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setExactCompanyCheckDialog(false);
                  setFoundExactCompany(null);
                  setPendingSyncUserId(null);
                }}
              >
                Annuleren
              </Button>
              <Button 
                variant="outline"
                onClick={async () => {
                  // Create new company
                  if (pendingSyncUserId) {
                    await performSync(pendingSyncUserId, false);
                  }
                }}
                disabled={exactLoading}
              >
                {exactLoading ? 'Aanmaken...' : 'Nieuw bedrijf aanmaken'}
              </Button>
              <Button 
                onClick={async () => {
                  // Use existing company
                  if (pendingSyncUserId) {
                    await performSync(pendingSyncUserId, true);
                  }
                }}
                disabled={exactLoading}
              >
                {exactLoading ? 'Synchroniseren...' : 'Bestaand bedrijf gebruiken'}
              </Button>
            </div>
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
          </DialogPortal>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
          <DialogContent className="max-w-2xl w-[95vw] md:w-[720px] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Create a new user account. A secure password will be generated automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="user@example.com"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="full_name">Full Name</Label>
                <Input
                  id="full_name"
                  placeholder="John Doe"
                  value={newUser.full_name}
                  onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(value) => setNewUser({ ...newUser, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                    <SelectItem value="reseller">Reseller</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="company_id">Company ID (Optional)</Label>
                <Input
                  id="company_id"
                  placeholder="Company ID"
                  value={newUser.company_id}
                  onChange={(e) => setNewUser({ ...newUser, company_id: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="email_template_suffix">Email Template Suffix</Label>
                <Select
                  value={newUser.email_template_suffix || 'none'}
                  onValueChange={(value) => setNewUser({ ...newUser, email_template_suffix: value === 'none' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select template suffix" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (default templates)</SelectItem>
                    <SelectItem value="mlp">MLP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Bedrijfsgegevens */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label>Bedrijfsnaam</Label>
                  <Input
                    placeholder="Bedrijfsnaam"
                    value={newUser.company_name}
                    onChange={(e) => setNewUser({ ...newUser, company_name: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Straat</Label>
                  <Input
                    placeholder="Straat"
                    value={newUser.company_street}
                    onChange={(e) => setNewUser({ ...newUser, company_street: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Nummer</Label>
                  <Input
                    placeholder="Nummer"
                    value={newUser.company_number}
                    onChange={(e) => setNewUser({ ...newUser, company_number: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Postcode</Label>
                  <Input
                    placeholder="Postcode"
                    value={newUser.company_postal_code}
                    onChange={(e) => setNewUser({ ...newUser, company_postal_code: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Stad</Label>
                  <Input
                    placeholder="Stad"
                    value={newUser.company_city}
                    onChange={(e) => setNewUser({ ...newUser, company_city: e.target.value })}
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>BTW Nummer</Label>
                  <Input
                    placeholder="BTW Nummer"
                    value={newUser.company_vat_number}
                    onChange={(e) => setNewUser({ ...newUser, company_vat_number: e.target.value })}
                  />
                </div>
              </div>

              {/* BTW Nummer Lookup Section for New User */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium mb-2">BTW Nummer Lookup</h4>
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Enter BTW nummer (e.g., BE0123456789)"
                      value={vatNumber}
                      onChange={(e) => setVatNumber(e.target.value)}
                      className="flex-1"
                    />
                    <Button
                      onClick={lookupVatNumber}
                      disabled={vatLookupLoading}
                      variant="outline"
                    >
                      {vatLookupLoading ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Looking up...
                        </>
                      ) : (
                        <>
                          <Search className="h-4 w-4 mr-2" />
                          Lookup
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {vatLookupError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{vatLookupError}</p>
                    </div>
                  )}
                  
                  {vatLookupData && vatLookupData.is_valid && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                      <h5 className="font-medium text-green-800 mb-2">Company Found:</h5>
                      <div className="space-y-1 text-sm">
                        {vatLookupData.company_name && (
                          <p><strong>Name:</strong> {vatLookupData.company_name}</p>
                        )}
                        {vatLookupData.street && (
                          <p><strong>Street:</strong> {vatLookupData.street}</p>
                        )}
                        {vatLookupData.street_number && (
                          <p><strong>Number:</strong> {vatLookupData.street_number}</p>
                        )}
                        {vatLookupData.postal_code && (
                          <p><strong>Postal Code:</strong> {vatLookupData.postal_code}</p>
                        )}
                        {vatLookupData.city && (
                          <p><strong>City:</strong> {vatLookupData.city}</p>
                        )}
                        {vatLookupData.vat_number && (
                          <p><strong>BTW Nummer:</strong> {vatLookupData.vat_number}</p>
                        )}
                        {vatLookupData.address && (
                          <p><strong>Full Address:</strong> {vatLookupData.address}</p>
                        )}
                      </div>
                      <Button
                        onClick={() => applyVatDataToNewUser(vatLookupData)}
                        className="mt-3"
                        size="sm"
                      >
                        Apply to New User
                      </Button>
                    </div>
                  )}
                  
                  {vatLookupData && !vatLookupData.is_valid && vatLookupData.error_message && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{vatLookupData.error_message}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="send_email"
                  checked={newUser.send_credentials_email}
                  onCheckedChange={(checked) => setNewUser({ ...newUser, send_credentials_email: checked })}
                />
                <Label htmlFor="send_email">Send credentials via email</Label>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => {
                  setShowCreateUser(false);
                  // Clear VAT lookup data when closing
                  setVatLookupData(null);
                  setVatLookupError(null);
                  setVatNumber('');
                }}>
                  Cancel
                </Button>
                <Button onClick={createUser}>
                  <UserPlus className="h-4 w-4 mr-1" />
                  Create User
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete this user? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {userToDelete && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <div className="font-medium text-red-800">
                    {userToDelete.full_name}
                  </div>
                  <div className="text-sm text-red-600">
                    {userToDelete.email}
                  </div>
                  <div className="text-sm text-red-600">
                    Role: {userToDelete.role.replace('_', ' ').toUpperCase()}
                  </div>
                </div>
              )}
              
              <div className="text-sm text-gray-600">
                This will permanently delete the user and all associated data. This action cannot be undone.
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => userToDelete && deleteUser(userToDelete.id)}
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete User
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Permission Dialog */}
        <Dialog open={showAddPermission} onOpenChange={(open) => {
          setShowAddPermission(open);
          if (!open) {
            // Clear all state when dialog is closed
            setZipcodeSearch('');
            setCities([]);
            setNewPermission({
              user_id: selectedUserForAccess?.id || '',
              permission_type: 'province',
              province_ids: [],
              city_ids: []
            });
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Location Permission</DialogTitle>
              <DialogDescription>
                Grant access to a specific province or city for MijnPerceel projects
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="permission-type">Permission Type</Label>
                <Select
                  value={newPermission.permission_type}
                  onValueChange={(value) => {
                    setNewPermission({ ...newPermission, permission_type: value, province_ids: [], city_ids: [] });
                    setZipcodeSearch('');  // Clear zipcode search when switching permission type
                    setCities([]);  // Clear cities when switching permission type
                    // Load provinces for Vlaanderen region (ID: 1) for both types
                    loadProvinces('1');
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="province">Province Access</SelectItem>
                    <SelectItem value="city">City Access</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {newPermission.permission_type === 'province' && (
                <div>
                  <Label htmlFor="province-select">Select Provinces (multiple)</Label>
                  <div className="border rounded-md p-4 max-h-60 overflow-y-auto">
                    {provinces.length === 0 ? (
                      <p className="text-sm text-gray-500">Loading provinces...</p>
                    ) : (
                      provinces.map((province) => (
                        <div key={province.id} className="flex items-center space-x-2 py-2">
                          <input
                            type="checkbox"
                            id={`province-${province.id}`}
                            checked={newPermission.province_ids.includes(province.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setNewPermission({
                                  ...newPermission,
                                  province_ids: [...newPermission.province_ids, province.id]
                                });
                              } else {
                                setNewPermission({
                                  ...newPermission,
                                  province_ids: newPermission.province_ids.filter(id => id !== province.id)
                                });
                              }
                            }}
                            className="h-4 w-4 rounded border-gray-300"
                          />
                          <label htmlFor={`province-${province.id}`} className="text-sm cursor-pointer">
                            {province.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                  {newPermission.province_ids.length > 0 && (
                    <p className="text-sm text-gray-600 mt-2">
                      Selected: {newPermission.province_ids.length} province(s)
                    </p>
                  )}
                </div>
              )}

              {newPermission.permission_type === 'city' && (
                <>
                  <div>
                    <Label htmlFor="province-select-for-city">Select Provinces (multiple)</Label>
                    <div className="border rounded-md p-4 max-h-48 overflow-y-auto">
                      {provinces.length === 0 ? (
                        <p className="text-sm text-gray-500">Loading provinces...</p>
                      ) : (
                        provinces.map((province) => (
                          <div key={province.id} className="flex items-center space-x-2 py-2">
                            <input
                              type="checkbox"
                              id={`province-for-city-${province.id}`}
                              checked={newPermission.province_ids.includes(province.id)}
                              onChange={(e) => {
                                const newProvinceIds = e.target.checked
                                  ? [...newPermission.province_ids, province.id]
                                  : newPermission.province_ids.filter(id => id !== province.id);
                                
                                setNewPermission({
                                  ...newPermission,
                                  province_ids: newProvinceIds,
                                  city_ids: [] // Clear selected cities when provinces change
                                });
                                
                                // Reload cities for all selected provinces
                                if (newProvinceIds.length > 0) {
                                  loadCitiesForProvinces(newProvinceIds, zipcodeSearch);
                                } else {
                                  setCities([]); // Clear cities if no provinces selected
                                }
                              }}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                            <label htmlFor={`province-for-city-${province.id}`} className="text-sm cursor-pointer">
                              {province.name}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                    {newPermission.province_ids.length > 0 && (
                      <p className="text-sm text-gray-600 mt-2">
                        Selected: {newPermission.province_ids.length} province(s)
                      </p>
                    )}
                  </div>
                  
                  {newPermission.province_ids.length > 0 && (
                    <div>
                      <Label htmlFor="city-select">Select Cities (multiple)</Label>
                      
                      {/* Zipcode Search Input */}
                      <div className="mb-3">
                        <div className="relative">
                          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search by zipcode..."
                            value={zipcodeSearch}
                            onChange={(e) => {
                              const value = e.target.value;
                              setZipcodeSearch(value);
                              // Reload cities with search filter
                              if (newPermission.province_ids.length > 0) {
                                loadCitiesForProvinces(newPermission.province_ids, value);
                              }
                            }}
                            className="pl-8"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Type a zipcode to filter cities (e.g., "9000" or "9")
                        </p>
                      </div>

                      <div className="border rounded-md p-4 max-h-60 overflow-y-auto">
                        {cities.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            {zipcodeSearch ? 'No cities found matching your search...' : 'Select provinces above to load cities...'}
                          </p>
                        ) : (
                          cities.map((city) => (
                            <div key={city.id} className="flex items-center space-x-2 py-2">
                              <input
                                type="checkbox"
                                id={`city-${city.id}`}
                                checked={newPermission.city_ids.includes(city.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setNewPermission({
                                      ...newPermission,
                                      city_ids: [...newPermission.city_ids, city.id]
                                    });
                                  } else {
                                    setNewPermission({
                                      ...newPermission,
                                      city_ids: newPermission.city_ids.filter(id => id !== city.id)
                                    });
                                  }
                                }}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <label htmlFor={`city-${city.id}`} className="text-sm cursor-pointer">
                                {city.name} ({city.zipcode})
                              </label>
                            </div>
                          ))
                        )}
                      </div>
                      {newPermission.city_ids.length > 0 && (
                        <p className="text-sm text-gray-600 mt-2">
                          Selected: {newPermission.city_ids.length} city/cities
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => {
                  setShowAddPermission(false);
                  setZipcodeSearch('');
                  setCities([]);
                  setNewPermission({
                    user_id: selectedUserForAccess?.id || '',
                    permission_type: 'province',
                    province_ids: [],
                    city_ids: []
                  });
                }}>
                  Cancel
                </Button>
                <Button onClick={createUserPermission}>
                  Add Permission
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Category Permission Dialog */}
        <Dialog open={showAddCategoryPermission} onOpenChange={(open) => {
          setShowAddCategoryPermission(open);
          if (!open) {
            setSelectedCategories([]);
            setCategorySearch('');
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add Category Permissions</DialogTitle>
              <DialogDescription>
                Select one or more project categories that the user can access
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search categories..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="pl-8"
                />
              </div>

              {/* Select All Controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const allAvailableCategories = categories
                        .filter(category => !userCategoryPermissions.some(perm => perm.category_id === category.id))
                        .map(category => category.id);
                      setSelectedCategories(allAvailableCategories);
                    }}
                    className="text-xs"
                  >
                    Select All Available
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedCategories([])}
                    className="text-xs"
                  >
                    Clear All
                  </Button>
                </div>
                <div className="text-sm text-gray-600">
                  {selectedCategories.length} selected
                </div>
              </div>

              {/* Category List */}
              <div className="border rounded-md p-4 max-h-96 overflow-y-auto">
                {categories.length === 0 ? (
                  <div className="text-sm text-gray-500 py-4">
                    <p className="font-medium mb-2">No categories available</p>
                    <p className="text-xs">
                      The categories table is empty. Please run the <code className="bg-gray-100 px-1 rounded">populate_categories.py</code> script to populate the categories database.
                    </p>
                  </div>
                ) : (
                  Object.entries(getCategoriesByGroup())
                    .filter(([groupId, groupData]) => {
                      // Filter groups based on search term
                      if (categorySearch === '') return true;
                      const groupMatches = groupData.group.name.toLowerCase().includes(categorySearch.toLowerCase());
                      const categoryMatches = groupData.categories.some(cat => 
                        cat.name.toLowerCase().includes(categorySearch.toLowerCase())
                      );
                      return groupMatches || categoryMatches;
                    })
                    .map(([groupId, groupData]) => {
                      const isExpanded = expandedGroups.has(groupId);
                      const filteredCategories = groupData.categories.filter(category => 
                        categorySearch === '' || 
                        category.name.toLowerCase().includes(categorySearch.toLowerCase())
                      );
                      
                      if (filteredCategories.length === 0) return null;
                      
                      return (
                        <div key={groupId} className="mb-4">
                          {/* Group Header */}
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div 
                                className="flex items-center space-x-2 cursor-pointer hover:bg-gray-100 rounded p-1 -m-1 transition-colors flex-1"
                                onClick={() => toggleGroupExpansion(groupId)}
                              >
                                <div className={`transform transition-transform ${isExpanded ? 'rotate-90' : ''}`}>
                                  ▶
                                </div>
                                <div>
                                  <h4 className="font-medium text-sm text-gray-900">{groupData.group.name}</h4>
                                  {groupData.group.description && (
                                    <p className="text-xs text-gray-500">{groupData.group.description}</p>
                                  )}
                                  <p className="text-xs text-gray-400">
                                    {filteredCategories.length} categor{filteredCategories.length !== 1 ? 'ies' : 'y'}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Select All Checkbox */}
                              <div className="flex items-center space-x-2 ml-4">
                                <input
                                  type="checkbox"
                                  id={`select-all-${groupId}`}
                                  checked={areAllCategoriesInGroupSelected(groupId, groupData, categorySearch)}
                                  ref={(input) => {
                                    if (input) {
                                      input.indeterminate = areSomeCategoriesInGroupSelected(groupId, groupData, categorySearch);
                                    }
                                  }}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleSelectAllGroup(groupId, groupData, categorySearch, e.target.checked);
                                  }}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                                <label 
                                  htmlFor={`select-all-${groupId}`}
                                  className="text-xs text-gray-600 cursor-pointer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Select All
                                </label>
                              </div>
                              
                              <div className="text-xs text-gray-400 ml-2">
                                {isExpanded ? 'Click to collapse' : 'Click to expand'}
                              </div>
                            </div>
                          </div>
                          
                          {/* Group Categories */}
                          {isExpanded && (
                            <div className="mt-2 ml-4 space-y-1">
                              {filteredCategories.map((category) => {
                                const isAlreadyAssigned = userCategoryPermissions.some(
                                  perm => perm.category_id === category.id
                                );
                                
                                return (
                                  <div key={category.id} className="flex items-center space-x-2 py-2 px-3 border rounded hover:bg-gray-50">
                                    <input
                                      type="checkbox"
                                      id={`category-${category.id}`}
                                      checked={selectedCategories.includes(category.id)}
                                      disabled={isAlreadyAssigned}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setSelectedCategories([...selectedCategories, category.id]);
                                        } else {
                                          setSelectedCategories(selectedCategories.filter(id => id !== category.id));
                                        }
                                      }}
                                      className="h-4 w-4 rounded border-gray-300"
                                    />
                                    <label 
                                      htmlFor={`category-${category.id}`} 
                                      className={`text-sm cursor-pointer flex-1 ${isAlreadyAssigned ? 'text-gray-400 line-through' : ''}`}
                                    >
                                      {category.name}
                                      {isAlreadyAssigned && (
                                        <span className="ml-2 text-xs text-gray-500">(Already assigned)</span>
                                      )}
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })
                    .filter(Boolean) // Remove null entries
                )}
              </div>

              {selectedCategories.length > 0 && (
                <p className="text-sm text-gray-600">
                  Selected: {selectedCategories.length} category/categories
                </p>
              )}

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => {
                  setShowAddCategoryPermission(false);
                  setSelectedCategories([]);
                  setCategorySearch('');
                }}>
                  Cancel
                </Button>
                <Button onClick={createUserCategoryPermissions} disabled={selectedCategories.length === 0}>
                  Add {selectedCategories.length > 0 ? `${selectedCategories.length} ` : ''}Permission{selectedCategories.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// Subscription Management Component
const SubscriptionManagement = ({ API, users, refreshUsers }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
  const [history, setHistory] = useState([]);
  const [newSubscription, setNewSubscription] = useState({
    user_id: '',
    start_date: '',
    end_date: '',
    auto_renew: true
  });
  const [skipExactOnline, setSkipExactOnline] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  // Exact item selection state
  const [exactGroups, setExactGroups] = useState([]);
  const [exactItems, setExactItems] = useState([]);
  const [exactSubTypes, setExactSubTypes] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedSubTypeId, setSelectedSubTypeId] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedUnitPrice, setSelectedUnitPrice] = useState('');
  const [exactLoading, setExactLoading] = useState(false);
  // Additional subscription lines for multi-line subscriptions
  const [subscriptionLines, setSubscriptionLines] = useState([]);
  const [oneTimeLines, setOneTimeLines] = useState([]);
  const [selectedGroupIdSubs, setSelectedGroupIdSubs] = useState('');
  const [selectedGroupIdOne, setSelectedGroupIdOne] = useState('');
  const [exactItemsSubs, setExactItemsSubs] = useState([]);
  const [exactItemsOne, setExactItemsOne] = useState([]);
  const [selectedResellerId, setSelectedResellerId] = useState('none');
  const [resellers, setResellers] = useState([]);

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  useEffect(() => {
    const initExactLists = async () => {
      setExactLoading(true);
      try {
        // Load default group codes
        let defaults = { subscription_group_code: null, onetime_group_code: null };
        try {
          const dRes = await fetch(`${API}/admin/exact-online/defaults/item-group-codes`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          if (dRes.ok) {
            defaults = await dRes.json();
          }
        } catch {}

        // Load groups (for dropdowns)
        const gRes = await fetch(`${API}/admin/exact-online/item-groups`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (gRes.ok) {
          const groups = await gRes.json();
          setExactGroups(groups);
          // Resolve default groups by code
          const subPref = Array.isArray(groups) && defaults.subscription_group_code ? groups.find(g => g.Code === defaults.subscription_group_code) : null;
          const onePref = Array.isArray(groups) && defaults.onetime_group_code ? groups.find(g => g.Code === defaults.onetime_group_code) : null;

          // Load subscription items
          if (subPref && subPref.ID) {
            setSelectedGroupIdSubs(subPref.ID);
            try {
              const iRes = await fetch(`${API}/admin/exact-online/items?group_id=${encodeURIComponent(subPref.ID)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
              });
              setExactItemsSubs(iRes.ok ? await iRes.json() : []);
            } catch { setExactItemsSubs([]); }
          } else if (defaults.subscription_group_code) {
            try {
              const iRes = await fetch(`${API}/admin/exact-online/items?group_code=${encodeURIComponent(defaults.subscription_group_code)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
              });
              setExactItemsSubs(iRes.ok ? await iRes.json() : []);
            } catch { setExactItemsSubs([]); }
          } else {
            setExactItemsSubs([]);
          }

          // Load one-time items
          if (onePref && onePref.ID) {
            setSelectedGroupIdOne(onePref.ID);
            try {
              const iRes2 = await fetch(`${API}/admin/exact-online/items?group_id=${encodeURIComponent(onePref.ID)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
              });
              setExactItemsOne(iRes2.ok ? await iRes2.json() : []);
            } catch { setExactItemsOne([]); }
          } else if (defaults.onetime_group_code) {
            try {
              const iRes2 = await fetch(`${API}/admin/exact-online/items?group_code=${encodeURIComponent(defaults.onetime_group_code)}`, {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
              });
              setExactItemsOne(iRes2.ok ? await iRes2.json() : []);
            } catch { setExactItemsOne([]); }
          } else {
            setExactItemsOne([]);
          }
        }
        // Load subscription types
        const tRes = await fetch(`${API}/admin/exact-online/subscription-types`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (tRes.ok) {
          const types = await tRes.json();
          setExactSubTypes(types);
        }
        // Ensure at least one line row exists for both sections
        setSubscriptionLines(prev => (Array.isArray(prev) && prev.length > 0) ? prev : [{ itemId: '', quantity: 1, unitPrice: '', description: '' }]);
        setOneTimeLines(prev => (Array.isArray(prev) && prev.length > 0) ? prev : [{ itemId: '', quantity: 1, unitPrice: '', description: '' }]);
      } catch (e) {
        console.error('Failed to load Exact lists', e);
      } finally {
        setExactLoading(false);
      }
    };
    const loadResellers = async () => {
      try {
        const response = await fetch(`${API}/admin/users?role=reseller&limit=100`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const resellerUsers = await response.json();
          setResellers(resellerUsers);
        }
      } catch (error) {
        console.error('Error loading resellers:', error);
      }
    };
    if (showCreateDialog) {
      initExactLists();
      loadResellers();
    } else {
      // Reset selection when dialog closes
      setSelectedGroupIdSubs('');
      setSelectedGroupIdOne('');
      setSelectedSubTypeId('');
      setExactItemsSubs([]);
      setExactItemsOne([]);
      setExactSubTypes([]);
      setSubscriptionLines([]);
      setOneTimeLines([]);
      setSelectedResellerId('none');
    }
  }, [showCreateDialog]);

  // Deprecated: per-section loaders are handled inline on value changes

  const fetchSubscriptionInfo = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/subscription/all`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const subscriptionData = await response.json();
        
        // Map subscriptions with user information
        const enrichedSubscriptions = subscriptionData.map(sub => {
          const user = users.find(u => u.id === sub.user_id);
          const now = new Date();
          const endDate = new Date(sub.end_date);
          const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
          
          return {
            ...sub,
            user_email: user?.email || 'Unknown',
            user_name: user?.full_name || 'Unknown',
            days_remaining: daysRemaining > 0 ? daysRemaining : 0,
            can_cancel: daysRemaining <= 90 && !sub.cancellation_requested
          };
        });
        
        setSubscriptions(enrichedSubscriptions);
      } else {
        console.error('Error fetching subscriptions');
      }
    } catch (error) {
      console.error('Error fetching subscription info:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSubscription = async () => {
    setProcessing(true);
    setResult(null);
    
    try {
      // Build lines payload from multi-line editors
      const linesPayload = [];
      // Subscription (recurring) lines
      for (const ln of subscriptionLines) {
        if (!ln || !ln.itemId) continue;
        const qty = Math.max(1, parseInt(ln.quantity || 1, 10) || 1);
        const obj = { item_id: ln.itemId, quantity: qty };
        if (ln.unitPrice !== '' && ln.unitPrice != null) {
          const up2 = parseFloat(ln.unitPrice);
          if (!Number.isNaN(up2)) obj.unit_price = up2;
        }
        if (ln.description) obj.description = ln.description;
        // recurring by default; do not set one_time
        linesPayload.push(obj);
      }
      // One-time lines
      for (const ln of oneTimeLines) {
        if (!ln || !ln.itemId) continue;
        const qty = Math.max(1, parseInt(ln.quantity || 1, 10) || 1);
        const obj = { item_id: ln.itemId, quantity: qty, one_time: true };
        if (ln.unitPrice !== '' && ln.unitPrice != null) {
          const up2 = parseFloat(ln.unitPrice);
          if (!Number.isNaN(up2)) obj.unit_price = up2;
        }
        if (ln.description) obj.description = ln.description;
        linesPayload.push(obj);
      }

      const response = await fetch(`${API}/subscription/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newSubscription,
          exact_item_id: null,
          exact_unit_price: null,
          exact_subscription_type_id: selectedSubTypeId || null,
          lines: linesPayload.length ? linesPayload : null,
          reseller_user_id: selectedResellerId !== 'none' ? selectedResellerId : null,
          skip_exact_online: skipExactOnline
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResult({ type: 'success', message: 'Subscription created successfully!' });
        setShowCreateDialog(false);
        setNewSubscription({ user_id: '', start_date: '', end_date: '', auto_renew: true });
        setSelectedGroupId('');
        setSelectedItemId('');
        setSelectedSubTypeId('');
        setSelectedUnit('');
        setSelectedUnitPrice('');
        setSelectedResellerId('none');
        setSkipExactOnline(false);
        fetchSubscriptionInfo();
        refreshUsers();
        setSubscriptionLines([]);
        setOneTimeLines([]);
        setSelectedGroupIdSubs('');
        setSelectedGroupIdOne('');
        setExactItemsSubs([]);
        setExactItemsOne([]);
      } else {
        setResult({ type: 'error', message: data.detail || 'Failed to create subscription' });
      }
    } catch (error) {
      setResult({ type: 'error', message: 'Error creating subscription' });
    } finally {
      setProcessing(false);
    }
  };

  const viewHistory = async (userId) => {
    setSelectedUserForHistory(userId);
    setShowHistoryDialog(true);
    
    try {
      const response = await fetch(`${API}/subscription/admin/history/${userId}?limit=50`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setHistory(data);
      } else {
        console.error('Failed to fetch subscription history:', response.statusText);
        setHistory([]);
      }
    } catch (error) {
      console.error('Error fetching subscription history:', error);
      setHistory([]);
    }
  };

  const getStatusBadge = (subscription) => {
    if (!subscription.is_active) {
      return <Badge variant="destructive">Inactive</Badge>;
    }
    if (subscription.cancellation_requested) {
      return <Badge variant="outline" className="bg-yellow-50 text-yellow-800">Cancelling</Badge>;
    }
    if (subscription.days_remaining <= 30) {
      return <Badge variant="outline" className="bg-orange-50 text-orange-800">Expiring Soon</Badge>;
    }
    return <Badge variant="outline" className="bg-green-50 text-green-800">Active</Badge>;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
            <div className="flex items-center justify-between">
            <div>
              <CardTitle>Subscription Management</CardTitle>
              <CardDescription>
                Manage user subscriptions and access to the system
              </CardDescription>
            </div>
              <Button onClick={async () => { await (async () => {
                try {
                  setExactLoading(true);
                  const gRes = await fetch(`${API}/admin/exact-online/item-groups`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
                  });
                  if (gRes.ok) setExactGroups(await gRes.json());
                  const tRes = await fetch(`${API}/admin/exact-online/subscription-types`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
                  });
                  if (tRes.ok) setExactSubTypes(await tRes.json());
                } catch (e) {
                  console.error('Prefetch Exact lists failed', e);
                } finally {
                  setExactLoading(false);
                }
              })(); setShowCreateDialog(true); }} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              Create Subscription
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {result && (
            <div className={`p-4 mb-4 rounded-lg ${
              result.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {result.message}
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="text-lg">Loading subscriptions...</div>
            </div>
          ) : subscriptions.length > 0 ? (
            <div className="space-y-4">
              {subscriptions.map((sub) => (
                <div key={sub.id} className="p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold">{sub.user_name}</h3>
                        {getStatusBadge(sub)}
                        <span className="text-sm text-muted-foreground">
                          {sub.user_email}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Start:</span>
                          <div className="font-medium">
                            {new Date(sub.start_date).toLocaleDateString()}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">End:</span>
                          <div className="font-medium">
                            {new Date(sub.end_date).toLocaleDateString()}
                          </div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Days Remaining:</span>
                          <div className="font-medium">{sub.days_remaining}</div>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Auto Renew:</span>
                          <div className="font-medium">
                            {sub.auto_renew ? 'Yes' : 'No'}
                          </div>
                        </div>
                      </div>

                      {sub.cancellation_requested && (
                        <div className="mt-2 text-sm text-orange-600">
                          ⚠️ Cancellation requested on {new Date(sub.cancellation_requested_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => viewHistory(sub.user_id)}
                      className="ml-4"
                    >
                      <Clock className="w-4 h-4 mr-2" />
                      History
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No subscriptions found
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Subscription Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Create New Subscription</DialogTitle>
            <DialogDescription>
              Create a subscription for a user to grant access to the system
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="user">User</Label>
              <Select
                value={newSubscription.user_id}
                onValueChange={(value) => setNewSubscription({ ...newSubscription, user_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a user" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="datetime-local"
                value={newSubscription.start_date}
                onChange={(e) => setNewSubscription({ ...newSubscription, start_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="datetime-local"
                value={newSubscription.end_date}
                onChange={(e) => setNewSubscription({ ...newSubscription, end_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="reseller">Reseller (Optional)</Label>
              <Select
                value={selectedResellerId}
                onValueChange={(value) => setSelectedResellerId(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reseller (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Direct subscription)</SelectItem>
                  {resellers.map(reseller => (
                    <SelectItem key={reseller.id} value={reseller.id}>
                      {reseller.full_name} ({reseller.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                If selected, subscription will be linked to reseller's Exact Online account
              </p>
            </div>

            <div className="hidden">
              <Label>Article Group (moved to section below)</Label>
              <Select
                value={selectedGroupId}
                onValueChange={async (value) => {
                  setSelectedGroupId(value);
                  setSelectedItemId('');
                  setSelectedUnitPrice('');
                  try {
                    const params = new URLSearchParams();
                    if (value) params.set('group_id', value);
                    const url = `${API}/admin/exact-online/items${params.toString() ? `?${params.toString()}` : ''}`;
                    const iRes = await fetch(url, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    if (iRes.ok) {
                      const items = await iRes.json();
                      setExactItems(items);
                    } else {
                      setExactItems([]);
                    }
                  } catch (e) {
                    console.error('Failed to load items for group', e);
                    setExactItems([]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={exactLoading ? 'Loading groups...' : 'Select article group'} />
                </SelectTrigger>
                <SelectContent>
                  {exactGroups.map(g => (
                    <SelectItem key={g.ID} value={g.ID}>
                      {g.Description || g.Code || g.ID}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Removed single main item; using multi-line section below */}

            <div>
              <Label>Invoicing Terms (Subscription Type)</Label>
              <Select
                value={selectedSubTypeId}
                onValueChange={(value) => setSelectedSubTypeId(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={exactLoading ? 'Loading types...' : 'Select invoicing terms'} />
                </SelectTrigger>
                <SelectContent>
                  {exactSubTypes.map(t => (
                    <SelectItem key={t.ID} value={t.ID}>
                      {(t.Code ? `${t.Code} — ` : '') + (t.Description || t.ID)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Removed single unit price; unit price shown per line below */}

            {/* Subscription articles (multi-line) */}
            <div className="border rounded-md p-3 space-y-3">
              <div className="font-medium">Subscription articles</div>
              {/* Article Group selector for subscription articles */}
              <div>
                <Label>Article Group</Label>
                <Select
                  value={selectedGroupIdSubs}
                  onValueChange={async (value) => {
                    setSelectedGroupIdSubs(value);
                    try {
                      const params = new URLSearchParams();
                      if (value) params.set('group_id', value);
                      const url = `${API}/admin/exact-online/items${params.toString() ? `?${params.toString()}` : ''}`;
                      const iRes = await fetch(url, {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      if (iRes.ok) {
                        const items = await iRes.json();
                        setExactItemsSubs(items);
                      } else {
                        setExactItemsSubs([]);
                      }
                    } catch (e) {
                      console.error('Failed to load items for group', e);
                      setExactItemsSubs([]);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={exactLoading ? 'Loading groups...' : 'Select article group'} />
                  </SelectTrigger>
                  <SelectContent>
                    {exactGroups.map(g => (
                      <SelectItem key={g.ID} value={g.ID}>
                        {g.Description || g.Code || g.ID}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {subscriptionLines.length === 0 && (
                <div className="text-sm text-gray-600">No subscription articles. You can add lines below.</div>
              )}
              {subscriptionLines.map((ln, idx) => (
                <div key={idx} className="grid grid-cols-1 md-cols-5 gap-2 items-end">
                  <div className="md:col-span-2">
                    <Label>Item</Label>
                    <Select
                      value={ln.itemId || ''}
                      onValueChange={(val) => {
                        const item = exactItemsSubs.find(i => i.ID === val);
                        setSubscriptionLines(prev => prev.map((l, i) => i === idx ? {
                          ...l,
                          itemId: val,
                          unitPrice: (item && item.SalesPrice != null) ? String(item.SalesPrice) : ''
                        } : l));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={exactLoading ? 'Loading items...' : 'Select item'} />
                      </SelectTrigger>
                      <SelectContent>
                        {exactItemsSubs.map(it => (
                          <SelectItem key={it.ID} value={it.ID}>
                            {(it.Code ? `${it.Code} — ` : '') + (it.Description || it.ID)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min={1}
                      value={ln.quantity ?? 1}
                      onChange={(e) => setSubscriptionLines(prev => prev.map((l, i) => i === idx ? { ...l, quantity: e.target.value } : l))}
                    />
                  </div>
                  <div>
                    <Label>Unit Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={ln.unitPrice ?? ''}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setSubscriptionLines(prev => prev.filter((_, i) => i !== idx))}>Remove</Button>
                  </div>
                  <div className="md:col-span-5">
                    <Label>Description (optional)</Label>
                    <Input
                      value={ln.description || ''}
                      onChange={(e) => setSubscriptionLines(prev => prev.map((l, i) => i === idx ? { ...l, description: e.target.value } : l))}
                      placeholder="Custom description override"
                    />
                  </div>
                </div>
              ))}
              <div>
                <Button type="button" variant="secondary" onClick={() => setSubscriptionLines(prev => [...prev, { itemId: '', quantity: 1, unitPrice: '' }])}>+ Add line</Button>
              </div>
            </div>

            {/* One time (Einmalig) Articles */}
            <div className="border rounded-md p-3 space-y-3">
              <div className="font-medium">One time (Einmalig) Articles</div>
              {/* Article Group selector for one-time articles */}
              <div>
                <Label>Article Group</Label>
                <Select
                  value={selectedGroupIdOne}
                  onValueChange={async (value) => {
                    setSelectedGroupIdOne(value);
                    try {
                      const params = new URLSearchParams();
                      if (value) params.set('group_id', value);
                      const url = `${API}/admin/exact-online/items${params.toString() ? `?${params.toString()}` : ''}`;
                      const iRes = await fetch(url, {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      if (iRes.ok) {
                        const items = await iRes.json();
                        setExactItemsOne(items);
                      } else {
                        setExactItemsOne([]);
                      }
                    } catch (e) {
                      console.error('Failed to load items for group', e);
                      setExactItemsOne([]);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={exactLoading ? 'Loading groups...' : 'Select article group'} />
                  </SelectTrigger>
                  <SelectContent>
                    {exactGroups.map(g => (
                      <SelectItem key={g.ID} value={g.ID}>
                        {g.Description || g.Code || g.ID}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {oneTimeLines.length === 0 && (
                <div className="text-sm text-gray-600">No one-time articles. You can add lines below.</div>
              )}
              {oneTimeLines.map((ln, idx) => (
                <div key={idx} className="grid grid-cols-1 md-cols-5 gap-2 items-end">
                  <div className="md:col-span-2">
                    <Label>Item</Label>
                    <Select
                      value={ln.itemId || ''}
                      onValueChange={(val) => {
                        const item = exactItemsOne.find(i => i.ID === val);
                        setOneTimeLines(prev => prev.map((l, i) => i === idx ? {
                          ...l,
                          itemId: val,
                          unitPrice: (item && item.SalesPrice != null) ? String(item.SalesPrice) : ''
                        } : l));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={exactLoading ? 'Loading items...' : 'Select item'} />
                      </SelectTrigger>
                      <SelectContent>
                        {exactItemsOne.map(it => (
                          <SelectItem key={it.ID} value={it.ID}>
                            {(it.Code ? `${it.Code} — ` : '') + (it.Description || it.ID)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min={1}
                      value={ln.quantity ?? 1}
                      onChange={(e) => setOneTimeLines(prev => prev.map((l, i) => i === idx ? { ...l, quantity: e.target.value } : l))}
                    />
                  </div>
                  <div>
                    <Label>Unit Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={ln.unitPrice ?? ''}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOneTimeLines(prev => prev.filter((_, i) => i !== idx))}>Remove</Button>
                  </div>
                  <div className="md:col-span-5">
                    <Label>Description (optional)</Label>
                    <Input
                      value={ln.description || ''}
                      onChange={(e) => setOneTimeLines(prev => prev.map((l, i) => i === idx ? { ...l, description: e.target.value } : l))}
                      placeholder="Custom description override"
                    />
                  </div>
                </div>
              ))}
              <div>
                <Button type="button" variant="secondary" onClick={() => setOneTimeLines(prev => [...prev, { itemId: '', quantity: 1, unitPrice: '' }])}>+ Add line</Button>
              </div>
            </div>



            <div className="flex items-center space-x-2">
              <Switch
                id="auto_renew"
                checked={newSubscription.auto_renew}
                onCheckedChange={(checked) => setNewSubscription({ ...newSubscription, auto_renew: checked })}
              />
              <Label htmlFor="auto_renew">Auto Renew</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="skip_exact_online"
                checked={skipExactOnline}
                onCheckedChange={(checked) => setSkipExactOnline(checked)}
              />
              <Label htmlFor="skip_exact_online" className="cursor-pointer">
                Niet sturen naar Exact
              </Label>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Als aangevinkt, wordt de subscription alleen in de database opgeslagen en niet naar Exact Online gestuurd
            </p>

            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={createSubscription} disabled={
                processing ||
                !newSubscription.user_id ||
                !newSubscription.start_date ||
                !newSubscription.end_date ||
                !selectedSubTypeId ||
                !(
                  (subscriptionLines && subscriptionLines.some(l => l && l.itemId)) ||
                  (oneTimeLines && oneTimeLines.some(l => l && l.itemId))
                ) // need at least one article line
              }>
                {processing ? 'Creating...' : 'Create Subscription'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Subscription History</DialogTitle>
            <DialogDescription>
              View subscription history and events
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2">
            {history.length > 0 ? (
              history.map((item, index) => (
                <div key={index} className="p-3 border rounded-lg">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium capitalize">{item.action}</span>
                    <span className="text-sm text-muted-foreground">
                      {new Date(item.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No history available
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// User Subscription Management Component (for use in User Edit Dialog)
const UserSubscriptionManagement = ({ userId, userName, API, loadUsers }) => {
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingDates, setEditingDates] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [newSubscription, setNewSubscription] = useState({
    start_date: '',
    end_date: '',
    auto_renew: true
  });
  const [skipExactOnline, setSkipExactOnline] = useState(false);
  const [selectedResellerId, setSelectedResellerId] = useState('none');
  const [resellers, setResellers] = useState([]);
  // Exact item selection state
  const [exactGroups, setExactGroups] = useState([]);
  const [exactItems, setExactItems] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedUnitPrice, setSelectedUnitPrice] = useState('');
  const [exactLoading, setExactLoading] = useState(false);
  // NEW: subscription types
  const [exactSubTypes, setExactSubTypes] = useState([]);
  const [selectedSubTypeId, setSelectedSubTypeId] = useState('');
  // NEW: Exact subscription details
  const [exactDetails, setExactDetails] = useState(null);
  const [loadingExact, setLoadingExact] = useState(false);
  // Additional subscription lines for multi-line subscriptions (split into 2 sections)
  const [subscriptionLines, setSubscriptionLines] = useState([]);
  const [oneTimeLines, setOneTimeLines] = useState([]);
  // Separate group and items per section
  const [selectedGroupIdSubs, setSelectedGroupIdSubs] = useState('');
  const [selectedGroupIdOne, setSelectedGroupIdOne] = useState('');
  const [exactItemsSubs, setExactItemsSubs] = useState([]);
  const [exactItemsOne, setExactItemsOne] = useState([]);

  useEffect(() => {
    const initExactLists = async () => {
      try {
        // Always (re)load subscription types when opening the dialog
        try {
          const tRes = await fetch(`${API}/admin/exact-online/subscription-types`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          if (tRes.ok) {
            setExactSubTypes(await tRes.json());
          } else {
            setExactSubTypes([]);
          }
        } catch {
          setExactSubTypes([]);
        }

        // Load groups
        const gRes = await fetch(`${API}/admin/exact-online/item-groups`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (gRes.ok) {
          setExactGroups(await gRes.json());
        } else {
          setExactGroups([]);
        }
        // Load default codes
        let defaults = { subscription_group_code: null, onetime_group_code: null };
        try {
          const dRes = await fetch(`${API}/admin/exact-online/defaults/item-group-codes`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          if (dRes.ok) defaults = await dRes.json();
        } catch {}
        // Resolve defaults by code to IDs where possible and load items
        const subPref = Array.isArray(exactGroups) && defaults.subscription_group_code ? exactGroups.find(g => g.Code === defaults.subscription_group_code) : null;
        const onePref = Array.isArray(exactGroups) && defaults.onetime_group_code ? exactGroups.find(g => g.Code === defaults.onetime_group_code) : null;
        // Subscription items
        if (subPref && subPref.ID) {
          setSelectedGroupIdSubs(subPref.ID);
          const iRes = await fetch(`${API}/admin/exact-online/items?group_id=${encodeURIComponent(subPref.ID)}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
          });
          setExactItemsSubs(iRes.ok ? await iRes.json() : []);
        } else if (defaults.subscription_group_code) {
          const iRes = await fetch(`${API}/admin/exact-online/items?group_code=${encodeURIComponent(defaults.subscription_group_code)}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
          });
          setExactItemsSubs(iRes.ok ? await iRes.json() : []);
        }
        // One-time items
        if (onePref && onePref.ID) {
          setSelectedGroupIdOne(onePref.ID);
          const iRes2 = await fetch(`${API}/admin/exact-online/items?group_id=${encodeURIComponent(onePref.ID)}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
          });
          setExactItemsOne(iRes2.ok ? await iRes2.json() : []);
        } else if (defaults.onetime_group_code) {
          const iRes2 = await fetch(`${API}/admin/exact-online/items?group_code=${encodeURIComponent(defaults.onetime_group_code)}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
          });
          setExactItemsOne(iRes2.ok ? await iRes2.json() : []);
        }
        // Ensure at least one row in both sections
        setSubscriptionLines(prev => (Array.isArray(prev) && prev.length > 0) ? prev : [{ itemId: '', quantity: 1, unitPrice: '', description: '' }]);
        setOneTimeLines(prev => (Array.isArray(prev) && prev.length > 0) ? prev : [{ itemId: '', quantity: 1, unitPrice: '', description: '' }]);
      } catch {}
    };
    const loadResellers = async () => {
      try {
        const response = await fetch(`${API}/admin/users`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const allUsers = await response.json();
          const resellerUsers = allUsers.filter(u => u.role === 'reseller');
          setResellers(resellerUsers);
        }
      } catch (error) {
        console.error('Error loading resellers:', error);
      }
    };

    if (showCreateDialog) {
      initExactLists();
      loadResellers();
    } else {
      setSelectedGroupIdSubs('');
      setSelectedGroupIdOne('');
      setExactSubTypes([]);
      setExactItemsSubs([]);
      setExactItemsOne([]);
      setSubscriptionLines([]);
      setOneTimeLines([]);
      setSkipExactOnline(false);
    }
  }, [showCreateDialog]);

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchExactSubscriptionDetails();
  }, [userId]);

  const parseODataDate = (val) => {
    if (!val) return null;
    if (typeof val === 'string' && val.startsWith('/Date(')) {
      const m = /\/Date\((\d+)\)\//.exec(val);
      if (m && m[1]) {
        const ms = parseInt(m[1], 10);
        if (!isNaN(ms)) return new Date(ms);
      }
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  const fetchExactSubscriptionDetails = async () => {
    setLoadingExact(true);
    try {
      const res = await fetch(`${API}/subscription/admin/exact/details/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        setExactDetails(data);
      } else {
        setExactDetails(null);
      }
    } catch (e) {
      setExactDetails(null);
    } finally {
      setLoadingExact(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    setLoading(true);
    try {
      // For admin viewing another user's subscription, we need to simulate their session
      // We'll need to create an admin endpoint for this
      const response = await fetch(`${API}/subscription/admin/info/${userId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionInfo(data);
        if (data && data.subscription) {
          setNewStartDate(new Date(data.subscription.start_date).toISOString().slice(0, 16));
          setNewEndDate(new Date(data.subscription.end_date).toISOString().slice(0, 16));
        } else {
          setSubscriptionInfo(null);
        }
      } else {
        setSubscriptionInfo(null);
      }
    } catch (error) {
      console.error('Failed to fetch subscription info:', error);
      setSubscriptionInfo(null);
    } finally {
      setLoading(false);
    }
  };

  const saveDateChanges = async () => {
    setSaving(true);
    setResult(null);
    try {
      const response = await fetch(`${API}/subscription/admin/update/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          start_date: new Date(newStartDate).toISOString(),
          end_date: new Date(newEndDate).toISOString()
        })
      });

      if (response.ok) {
        setResult({ type: 'success', message: 'Subscription dates updated successfully!' });
        setEditingDates(false);
        fetchSubscriptionInfo();
        loadUsers();
      } else {
        const data = await response.json();
        setResult({ type: 'error', message: data.detail || 'Failed to update subscription dates' });
      }
    } catch (error) {
      setResult({ type: 'error', message: 'Error updating subscription dates' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    setResult(null);
    try {
      const response = await fetch(`${API}/subscription/admin/cancel/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setResult({ type: 'success', message: 'Subscription cancelled successfully.' });
        setShowCancelConfirm(false);
        fetchSubscriptionInfo();
        loadUsers();
      } else {
        const data = await response.json();
        setResult({ type: 'error', message: data.detail || 'Failed to cancel subscription' });
      }
    } catch (error) {
      setResult({ type: 'error', message: 'Error cancelling subscription' });
    } finally {
      setCancelling(false);
    }
  };

  const handleCreateSubscription = async () => {
    setCreating(true);
    setResult(null);
    try {
      // Build lines payload from multi-line editors
      const linesPayload = [];
      for (const ln of subscriptionLines) {
        if (!ln || !ln.itemId) continue;
        const qty = Math.max(1, parseInt(ln.quantity || 1, 10) || 1);
        const obj = { item_id: ln.itemId, quantity: qty };
        if (ln.unitPrice !== '' && ln.unitPrice != null) {
          const up2 = parseFloat(ln.unitPrice);
          if (!Number.isNaN(up2)) obj.unit_price = up2;
        }
        if (ln.description) obj.description = ln.description;
        linesPayload.push(obj);
      }
      for (const ln of oneTimeLines) {
        if (!ln || !ln.itemId) continue;
        const qty = Math.max(1, parseInt(ln.quantity || 1, 10) || 1);
        const obj = { item_id: ln.itemId, quantity: qty, one_time: true };
        if (ln.unitPrice !== '' && ln.unitPrice != null) {
          const up2 = parseFloat(ln.unitPrice);
          if (!Number.isNaN(up2)) obj.unit_price = up2;
        }
        if (ln.description) obj.description = ln.description;
        linesPayload.push(obj);
      }

      const response = await fetch(`${API}/subscription/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: userId,
          start_date: new Date(newSubscription.start_date).toISOString(),
          end_date: new Date(newSubscription.end_date).toISOString(),
          auto_renew: newSubscription.auto_renew,
          exact_item_id: null,
          exact_unit_price: null,
          exact_subscription_type_id: selectedSubTypeId || null,
          lines: linesPayload.length ? linesPayload : null,
          reseller_user_id: selectedResellerId && selectedResellerId !== 'none' ? selectedResellerId : null,
          skip_exact_online: skipExactOnline
        })
      });

      if (response.ok) {
        setResult({ type: 'success', message: 'Subscription created successfully!' });
        setShowCreateDialog(false);
        fetchSubscriptionInfo();
        // Immediately load Exact subscription details after creation
        fetchExactSubscriptionDetails();
        loadUsers();
        setSubscriptionLines([]);
        setOneTimeLines([]);
        setSelectedGroupIdSubs('');
        setSelectedGroupIdOne('');
        setExactItemsSubs([]);
        setExactItemsOne([]);
        setSelectedResellerId('none');
        setSkipExactOnline(false);
      } else {
        const data = await response.json();
        setResult({ type: 'error', message: data.detail || 'Failed to create subscription' });
      }
    } catch (error) {
      setResult({ type: 'error', message: 'Error creating subscription' });
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="border-t pt-4">
        <div className="text-center py-4 text-sm text-muted-foreground">Loading subscription...</div>
      </div>
    );
  }

  return (
    <div className="border-t pt-4">
      <h4 className="font-medium mb-2">Subscription Management</h4>
      
      {result && (
        <div className={`mb-3 p-3 rounded-lg text-sm ${
          result.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {result.message}
        </div>
      )}

      {subscriptionInfo?.subscription ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <Label className="text-xs">Start Date</Label>
              {editingDates ? (
                <Input
                  type="datetime-local"
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                  className="mt-1"
                />
              ) : (
                <div className="font-medium mt-1">
                  {new Date(subscriptionInfo.subscription.start_date).toLocaleDateString()}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">End Date</Label>
              {editingDates ? (
                <Input
                  type="datetime-local"
                  value={newEndDate}
                  onChange={(e) => setNewEndDate(e.target.value)}
                  className="mt-1"
                />
              ) : (
                <div className="font-medium mt-1">
                  {new Date(subscriptionInfo.subscription.end_date).toLocaleDateString()}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Days Remaining</Label>
              <div className="font-medium mt-1 text-emerald-600">{subscriptionInfo.days_remaining} days</div>
            </div>
            <div>
              <Label className="text-xs">Status</Label>
              <Badge variant={subscriptionInfo.subscription.is_active ? "default" : "destructive"} className="mt-1">
                {subscriptionInfo.subscription.is_active ? "Active" : "Inactive"}
              </Badge>
            </div>
          </div>

          {subscriptionInfo.subscription.cancellation_requested && (
            <div className="p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
              ⚠️ Cancellation requested. Will end on {new Date(subscriptionInfo.subscription.end_date).toLocaleDateString()}
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            {editingDates ? (
              <>
                <Button
                  size="sm"
                  onClick={saveDateChanges}
                  disabled={saving || !newStartDate || !newEndDate}
                  className="flex-1"
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingDates(false);
                    setResult(null);
                    // Reset to original values
                    if (subscriptionInfo && subscriptionInfo.subscription) {
                      setNewStartDate(new Date(subscriptionInfo.subscription.start_date).toISOString().slice(0, 16));
                      setNewEndDate(new Date(subscriptionInfo.subscription.end_date).toISOString().slice(0, 16));
                    }
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditingDates(true)}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit Dates
                </Button>
                {!subscriptionInfo.subscription.cancellation_requested && (
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setShowCancelConfirm(true)}
                  >
                    <XCircle className="w-3 h-3 mr-1" />
                    Cancel Subscription
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground text-center py-4">
            No active subscription found for this user
          </div>
          <Button
            size="sm"
            onClick={async () => {
              // Set default dates (start today, end 1 year later)
              const today = new Date();
              const oneYearLater = new Date(today);
              oneYearLater.setFullYear(today.getFullYear() + 1);
              setNewSubscription({
                start_date: today.toISOString().slice(0, 16),
                end_date: oneYearLater.toISOString().slice(0, 16),
                auto_renew: true
              });
              // Prefetch Exact lists before opening dialog
              try {
                setExactLoading(true);
                const gRes = await fetch(`${API}/admin/exact-online/item-groups`, {
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
                });
                if (gRes.ok) {
                  const groups = await gRes.json();
                  setExactGroups(groups);
                  // Auto-select preferred group by Code
                  const preferredCode = 'SubscriptionsPermitWatch';
                  const preferred = Array.isArray(groups) ? groups.find(g => g.Code === preferredCode) : null;
                  if (preferred && preferred.ID) {
                    setSelectedGroupId(preferred.ID);
                    // Load items for preferred group
                    const params = new URLSearchParams();
                    params.set('group_id', preferred.ID);
                    const url = `${API}/admin/exact-online/items?${params.toString()}`;
                    const iRes = await fetch(url, {
                      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
                    });
                    if (iRes.ok) setExactItems(await iRes.json());
                  }
                  // Ensure at least one line row exists
                  setExtraLines([{ itemId: '', quantity: 1, unitPrice: '', oneTime: false, description: '' }]);
                }
                const tRes = await fetch(`${API}/admin/exact-online/subscription-types`, {
                  headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
                });
                if (tRes.ok) setExactSubTypes(await tRes.json());
              } catch (e) {
                console.error('Prefetch Exact lists failed', e);
              } finally {
                setExactLoading(false);
              }
              setShowCreateDialog(true);
            }}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Subscription
          </Button>
        </div>
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel Subscription</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel {userName}'s subscription?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-gray-600 mb-4">
              The subscription will remain active until {subscriptionInfo?.subscription && new Date(subscriptionInfo.subscription.end_date).toLocaleDateString()}.
            </p>
            <p className="text-sm text-gray-600">
              The user will receive a notification email about the cancellation.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCancelConfirm(false)}>
              Keep Subscription
            </Button>
            <Button
              variant="destructive"
              onClick={handleCancelSubscription}
              disabled={cancelling}
            >
              {cancelling ? 'Cancelling...' : 'Yes, Cancel Subscription'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Subscription Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) {
          // Reset form when dialog closes
          setSelectedResellerId('none');
        }
      }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Create Subscription for {userName}</DialogTitle>
            <DialogDescription>
              Create a new subscription for this user
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="datetime-local"
                value={newSubscription.start_date}
                onChange={(e) => setNewSubscription({ ...newSubscription, start_date: e.target.value })}
              />
            </div>
            
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="datetime-local"
                value={newSubscription.end_date}
                onChange={(e) => setNewSubscription({ ...newSubscription, end_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="reseller">Reseller (Optional)</Label>
              <Select
                value={selectedResellerId}
                onValueChange={(value) => setSelectedResellerId(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select reseller (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Direct subscription)</SelectItem>
                  {resellers.map(reseller => (
                    <SelectItem key={reseller.id} value={reseller.id}>
                      {reseller.full_name} ({reseller.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                If selected, subscription will be linked to reseller's Exact Online account
              </p>
            </div>

            {/* NEW: Article group/item and invoicing type */}
            <div>
              <Label className="hidden">Article Group (moved to section below)</Label>
              <div className="hidden">
              <Select
                value={selectedGroupId}
                onValueChange={async (value) => {
                  setSelectedGroupId(value);
                  setSelectedItemId('');
                  setSelectedUnitPrice('');
                  try {
                    const params = new URLSearchParams();
                    if (value) params.set('group_id', value);
                    const url = `${API}/admin/exact-online/items${params.toString() ? `?${params.toString()}` : ''}`;
                    const iRes = await fetch(url, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                      }
                    });
                    if (iRes.ok) {
                      const items = await iRes.json();
                      setExactItems(items);
                    } else {
                      setExactItems([]);
                    }
                  } catch (e) {
                    console.error('Failed to load items for group', e);
                    setExactItems([]);
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder={exactLoading ? 'Loading groups...' : 'Select article group'} />
                </SelectTrigger>
                <SelectContent>
                  {exactGroups.map(g => (
                    <SelectItem key={g.ID} value={g.ID}>
                      {g.Description || g.Code || g.ID}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
            </div>

            {/* Removed single main item; using multi-line section below */}

            <div>
              <Label>Invoicing Terms (Subscription Type)</Label>
              <Select
                value={selectedSubTypeId}
                onValueChange={(value) => setSelectedSubTypeId(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={exactLoading ? 'Loading types...' : 'Select invoicing terms'} />
                </SelectTrigger>
                <SelectContent>
                  {exactSubTypes.map(t => (
                    <SelectItem key={t.ID} value={t.ID}>
                      {(t.Code ? `${t.Code} — ` : '') + (t.Description || t.ID)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Removed single unit price; unit price shown per line below */}

            {/* Subscription articles (multi-line) */}
            <div className="border rounded-md p-3 space-y-3">
              <div className="font-medium">Subscription articles</div>
              {/* Article Group selector for subscription articles */}
              <div>
                <Label>Article Group</Label>
                <Select
                  value={selectedGroupIdSubs}
                  onValueChange={async (value) => {
                    setSelectedGroupIdSubs(value);
                    try {
                      const params = new URLSearchParams();
                      if (value) params.set('group_id', value);
                      const url = `${API}/admin/exact-online/items${params.toString() ? `?${params.toString()}` : ''}`;
                      const iRes = await fetch(url, {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      if (iRes.ok) {
                        const items = await iRes.json();
                        setExactItemsSubs(items);
                      } else {
                        setExactItemsSubs([]);
                      }
                    } catch (e) {
                      console.error('Failed to load items for group', e);
                      setExactItemsSubs([]);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={exactLoading ? 'Loading groups...' : 'Select article group'} />
                  </SelectTrigger>
                  <SelectContent>
                    {exactGroups.map(g => (
                      <SelectItem key={g.ID} value={g.ID}>
                        {g.Description || g.Code || g.ID}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {subscriptionLines.length === 0 && (
                <div className="text-sm text-gray-600">No subscription articles. You can add lines below.</div>
              )}
              {subscriptionLines.map((ln, idx) => (
                <div key={idx} className="grid grid-cols-1 md-cols-5 gap-2 items-end">
                  <div className="md:col-span-2">
                    <Label>Item</Label>
                    <Select
                      value={ln.itemId || ''}
                      onValueChange={(val) => {
                        const item = exactItemsSubs.find(i => i.ID === val);
                        setSubscriptionLines(prev => prev.map((l, i) => i === idx ? {
                          ...l,
                          itemId: val,
                          unitPrice: (item && item.SalesPrice != null) ? String(item.SalesPrice) : ''
                        } : l));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={exactLoading ? 'Loading items...' : 'Select item'} />
                      </SelectTrigger>
                      <SelectContent>
                        {exactItemsSubs.map(it => (
                          <SelectItem key={it.ID} value={it.ID}>
                            {(it.Code ? `${it.Code} — ` : '') + (it.Description || it.ID)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min={1}
                      value={ln.quantity ?? 1}
                      onChange={(e) => setSubscriptionLines(prev => prev.map((l, i) => i === idx ? { ...l, quantity: e.target.value } : l))}
                    />
                  </div>
                  <div>
                    <Label>Unit Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={ln.unitPrice ?? ''}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setSubscriptionLines(prev => prev.filter((_, i) => i !== idx))}>Remove</Button>
                  </div>
                  <div className="md:col-span-5">
                    <Label>Description (optional)</Label>
                    <Input
                      value={ln.description || ''}
                      onChange={(e) => setSubscriptionLines(prev => prev.map((l, i) => i === idx ? { ...l, description: e.target.value } : l))}
                      placeholder="Custom description override"
                    />
                  </div>
                </div>
              ))}
              <div>
                <Button type="button" variant="secondary" onClick={() => setSubscriptionLines(prev => [...prev, { itemId: '', quantity: 1, unitPrice: '' }])}>+ Add line</Button>
              </div>
            </div>

            {/* One time (Einmalig) Articles */}
            <div className="border rounded-md p-3 space-y-3">
              <div className="font-medium">One time (Einmalig) Articles</div>
              {/* Article Group selector for one-time articles */}
              <div>
                <Label>Article Group</Label>
                <Select
                  value={selectedGroupIdOne}
                  onValueChange={async (value) => {
                    setSelectedGroupIdOne(value);
                    try {
                      const params = new URLSearchParams();
                      if (value) params.set('group_id', value);
                      const url = `${API}/admin/exact-online/items${params.toString() ? `?${params.toString()}` : ''}`;
                      const iRes = await fetch(url, {
                        headers: {
                          'Authorization': `Bearer ${localStorage.getItem('token')}`,
                          'Content-Type': 'application/json'
                        }
                      });
                      if (iRes.ok) {
                        const items = await iRes.json();
                        setExactItemsOne(items);
                      } else {
                        setExactItemsOne([]);
                      }
                    } catch (e) {
                      console.error('Failed to load items for group', e);
                      setExactItemsOne([]);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={exactLoading ? 'Loading groups...' : 'Select article group'} />
                  </SelectTrigger>
                  <SelectContent>
                    {exactGroups.map(g => (
                      <SelectItem key={g.ID} value={g.ID}>
                        {g.Description || g.Code || g.ID}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {oneTimeLines.length === 0 && (
                <div className="text-sm text-gray-600">No one-time articles. You can add lines below.</div>
              )}
              {oneTimeLines.map((ln, idx) => (
                <div key={idx} className="grid grid-cols-1 md-cols-5 gap-2 items-end">
                  <div className="md:col-span-2">
                    <Label>Item</Label>
                    <Select
                      value={ln.itemId || ''}
                      onValueChange={(val) => {
                        const item = exactItemsOne.find(i => i.ID === val);
                        setOneTimeLines(prev => prev.map((l, i) => i === idx ? {
                          ...l,
                          itemId: val,
                          unitPrice: (item && item.SalesPrice != null) ? String(item.SalesPrice) : ''
                        } : l));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={exactLoading ? 'Loading items...' : 'Select item'} />
                      </SelectTrigger>
                      <SelectContent>
                        {exactItemsOne.map(it => (
                          <SelectItem key={it.ID} value={it.ID}>
                            {(it.Code ? `${it.Code} — ` : '') + (it.Description || it.ID)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Quantity</Label>
                    <Input
                      type="number"
                      min={1}
                      value={ln.quantity ?? 1}
                      onChange={(e) => setOneTimeLines(prev => prev.map((l, i) => i === idx ? { ...l, quantity: e.target.value } : l))}
                    />
                  </div>
                  <div>
                    <Label>Unit Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={ln.unitPrice ?? ''}
                      readOnly
                      disabled
                    />
                  </div>
                  <div className="flex items-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setOneTimeLines(prev => prev.filter((_, i) => i !== idx))}>Remove</Button>
                  </div>
                  <div className="md:col-span-5">
                    <Label>Description (optional)</Label>
                    <Input
                      value={ln.description || ''}
                      onChange={(e) => setOneTimeLines(prev => prev.map((l, i) => i === idx ? { ...l, description: e.target.value } : l))}
                      placeholder="Custom description override"
                    />
                  </div>
                </div>
              ))}
              <div>
                <Button type="button" variant="secondary" onClick={() => setOneTimeLines(prev => [...prev, { itemId: '', quantity: 1, unitPrice: '' }])}>+ Add line</Button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="auto_renew"
                checked={newSubscription.auto_renew}
                onCheckedChange={(checked) => setNewSubscription({ ...newSubscription, auto_renew: checked })}
              />
              <Label htmlFor="auto_renew">Auto Renew</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="skip_exact_online_user"
                checked={skipExactOnline}
                onCheckedChange={(checked) => setSkipExactOnline(checked)}
              />
              <Label htmlFor="skip_exact_online_user" className="cursor-pointer">
                Niet sturen naar Exact
              </Label>
            </div>
            <p className="text-xs text-gray-500 ml-6">
              Als aangevinkt, wordt de subscription alleen in de database opgeslagen en niet naar Exact Online gestuurd
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateSubscription} disabled={creating || !newSubscription.start_date || !newSubscription.end_date || !selectedSubTypeId || !((subscriptionLines && subscriptionLines.some(l => l && l.itemId)) || (oneTimeLines && oneTimeLines.some(l => l && l.itemId)))}>
                {creating ? 'Creating...' : 'Create Subscription'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* NEW: Exact Online Details */}
      <div className="border-t pt-4 mt-4">
        <h4 className="font-medium mb-2">Exact Online</h4>
        {loadingExact ? (
          <div className="text-sm text-muted-foreground">Loading Exact details...</div>
        ) : exactDetails?.details ? (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Subscription Type</Label>
                <div className="mt-1">
                  {exactDetails.details.SubscriptionTypeDescription || exactDetails.details.SubscriptionTypeCode || exactDetails.details.SubscriptionType || '-'}
                </div>
              </div>
              <div>
                <Label className="text-xs">Exact Start Date</Label>
                <div className="mt-1">
                  {(() => { const d = parseODataDate(exactDetails.details.StartDate); return d ? d.toLocaleDateString() : '-'; })()}
                </div>
              </div>
              <div>
                <Label className="text-xs">Exact End Date</Label>
                <div className="mt-1">
                  {(() => { const d = parseODataDate(exactDetails.details.EndDate); return d ? d.toLocaleDateString() : '-'; })()}
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs">Lines</Label>
              <div className="mt-1 space-y-1">
                {(exactDetails.details.Lines || []).length === 0 ? (
                  <div className="text-muted-foreground">No lines</div>
                ) : (
                  (exactDetails.details.Lines || []).map((ln, idx) => (
                    <div key={idx} className="p-2 rounded border">
                      <div><span className="text-muted-foreground">Description:</span> {ln.Description || '-'}</div>
                      <div><span className="text-muted-foreground">Item:</span> {ln.Item || '-'}</div>
                      <div><span className="text-muted-foreground">Quantity:</span> {ln.Quantity != null ? ln.Quantity : '-'}</div>
                      <div><span className="text-muted-foreground">UnitPrice:</span> {ln.UnitPrice != null ? ln.UnitPrice : '-'}</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-muted-foreground">No Exact subscription linked.</div>
            <ExactLinker userId={userId} API={API} onLinked={() => fetchExactSubscriptionDetails()} />
          </div>
        )}
      </div>
    </div>
  );
};

const ExactLinker = ({ userId, API, onLinked }) => {
  const [subId, setSubId] = useState('');
  const [linking, setLinking] = useState(false);
  const handleLink = async () => {
    try {
      setLinking(true);
      const res = await fetch(`${API}/subscription/admin/exact/link/${userId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ subscription_id: subId.trim() })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.detail || 'Failed to link Exact subscription');
      } else {
        setSubId('');
        onLinked && onLinked();
      }
    } catch (e) {
      alert('Failed to link Exact subscription');
    } finally {
      setLinking(false);
    }
  };
  return (
    <div className="flex items-end gap-2">
      <div className="flex-1">
        <Label className="text-xs">Exact Subscription ID (GUID)</Label>
        <Input value={subId} onChange={(e) => setSubId(e.target.value)} placeholder="e.g., 123e4567-e89b-12d3-a456-426614174000" />
      </div>
      <Button onClick={handleLink} disabled={linking || !subId.trim()}>Link</Button>
    </div>
  );
};

export default AdminDashboard;
